import { Question } from './practiceTestQuestions';

// NY practice exam: 300-question bank (150 National + 150 NY-specific)
// Each new session randomly selects 75 questions from this bank

export const practiceTestQuestions: Question[] = [
  {
    questionNumber: 1,
    question: "Which of the following is considered real property?",
    answerOptions: [
      { text: "A built-in dishwasher attached to kitchen cabinetry", rationale: "An item that is attached and intended to remain with the property is generally treated as real property rather than personal property.", isCorrect: true },
      { text: "A freestanding refrigerator not attached to the wall", rationale: "A freestanding appliance is usually personal property unless the facts show it became a fixture.", isCorrect: false },
      { text: "A tenant's removable display case", rationale: "That is more likely a trade fixture or personal property than real property.", isCorrect: false },
      { text: "Patio furniture resting loose on the deck", rationale: "Loose outdoor furniture is ordinarily personal property.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 2,
    question: "Which legal description method is most commonly used in a recorded subdivision?",
    answerOptions: [
      { text: "Lot and block", rationale: "The recorded plat, or lot-and-block, method is commonly used for subdivided land where lots and blocks are identified on a filed map.", isCorrect: true },
      { text: "Metes and bounds", rationale: "Metes and bounds is commonly used for irregular parcels, not specifically because the land is in a recorded subdivision.", isCorrect: false },
      { text: "Rectangular survey township-and-range only", rationale: "Government survey descriptions are used in many states but are not the standard subdivision-plat answer here.", isCorrect: false },
      { text: "PITI description", rationale: "PITI is a financing term, not a legal description method.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 3,
    question: "A property owner whose land borders a flowing stream generally has:",
    answerOptions: [
      { text: "Riparian rights", rationale: "Riparian rights are associated with land bordering flowing water such as rivers and streams.", isCorrect: true },
      { text: "Littoral rights only", rationale: "Littoral rights are generally associated with lakes, seas, and oceans rather than flowing streams.", isCorrect: false },
      { text: "Air rights only", rationale: "Air rights concern the space above land, not water rights.", isCorrect: false },
      { text: "No water-related rights", rationale: "Bordering a stream typically creates riparian rights.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 4,
    question: "Which of the following is an example of an encumbrance?",
    answerOptions: [
      { text: "An easement allowing utility access", rationale: "An easement burdens title or use and is a classic example of an encumbrance.", isCorrect: true },
      { text: "Fresh interior paint", rationale: "Paint affects condition, not title or legal use rights.", isCorrect: false },
      { text: "A new roof warranty", rationale: "A warranty may be beneficial but is not itself an encumbrance on title.", isCorrect: false },
      { text: "A tenant's sofa", rationale: "Personal property inside the premises is not an encumbrance.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 5,
    question: "Which form of co-ownership includes a right of survivorship and usually equal undivided interests?",
    answerOptions: [
      { text: "Joint tenancy", rationale: "Joint tenancy is commonly tested as the co-ownership form with equal interests and survivorship.", isCorrect: true },
      { text: "Tenancy in common", rationale: "Tenancy in common generally does not include survivorship and may involve unequal shares.", isCorrect: false },
      { text: "Severalty", rationale: "Severalty means sole ownership, not co-ownership.", isCorrect: false },
      { text: "Leasehold", rationale: "Leasehold is a possession estate, not a co-ownership form.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 6,
    question: "A government taking of private property for public use with just compensation is based on:",
    answerOptions: [
      { text: "Eminent domain", rationale: "Eminent domain is the power to take property for public use, while condemnation is the process used to carry it out.", isCorrect: true },
      { text: "Escheat", rationale: "Escheat applies when an owner dies without heirs and without a valid will.", isCorrect: false },
      { text: "Police power", rationale: "Police power regulates use; it is not the taking power itself.", isCorrect: false },
      { text: "Accretion", rationale: "Accretion is a natural land-change concept, not a government power.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 7,
    question: "Zoning is primarily an exercise of which government power?",
    answerOptions: [
      { text: "Police power", rationale: "Zoning is used to protect public health, safety, morals, and general welfare, which is the classic definition of police power.", isCorrect: true },
      { text: "Eminent domain", rationale: "Eminent domain concerns taking property, not regulating use.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat concerns transfer of ownership to the state when there are no heirs.", isCorrect: false },
      { text: "Taxation", rationale: "Taxation raises public revenue rather than regulating land use.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 8,
    question: "Which appraisal approach is generally most applicable to a single-family residence in an active market?",
    answerOptions: [
      { text: "Sales comparison approach", rationale: "For owner-occupied residential property, the sales comparison approach usually reflects the way buyers and sellers make decisions.", isCorrect: true },
      { text: "Income approach", rationale: "The income approach is generally more relevant to income-producing property.", isCorrect: false },
      { text: "Gross income approach only", rationale: "That is not one of the standard three classic appraisal approaches.", isCorrect: false },
      { text: "Mortgage-balance approach", rationale: "Loan balance is not a recognized valuation approach.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 9,
    question: "The income approach to value is most closely associated with:",
    answerOptions: [
      { text: "Income-producing property", rationale: "The income approach is based on the property's earning power and is therefore especially important for investment property.", isCorrect: true },
      { text: "Vacant farmland only", rationale: "The approach is not limited to farmland.", isCorrect: false },
      { text: "Newly built homes only", rationale: "New construction may fit the cost approach more directly than the income approach.", isCorrect: false },
      { text: "Tax assessments only", rationale: "Tax assessment is not the defining context for the income approach.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 10,
    question: "If a property's annual NOI is $48,000 and the capitalization rate is 8%, the indicated value is:",
    answerOptions: [
      { text: "$600,000", rationale: "Value equals NOI divided by the capitalization rate. $48,000 / 0.08 = $600,000.", isCorrect: true },
      { text: "$384,000", rationale: "That does not result from dividing NOI by the cap rate.", isCorrect: false },
      { text: "$60,000", rationale: "That is far too low and reflects an incorrect operation.", isCorrect: false },
      { text: "$6,000,000", rationale: "That overstates value by shifting the decimal point incorrectly.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 11,
    question: "Private mortgage insurance (PMI) is primarily intended to protect the:",
    answerOptions: [
      { text: "Lender", rationale: "PMI protects the lender against certain default risk on qualifying high-LTV conventional loans.", isCorrect: true },
      { text: "Borrower", rationale: "The borrower pays for PMI in many cases, but the protection primarily benefits the lender.", isCorrect: false },
      { text: "Seller", rationale: "The seller is not the primary beneficiary of PMI.", isCorrect: false },
      { text: "Appraiser", rationale: "An appraiser is not protected by mortgage insurance.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 12,
    question: "Which term refers to principal, interest, taxes, and insurance?",
    answerOptions: [
      { text: "PITI", rationale: "PITI is the standard acronym used for principal, interest, taxes, and insurance in mortgage-payment discussions.", isCorrect: true },
      { text: "PMI", rationale: "PMI stands for private mortgage insurance, not the full payment bundle.", isCorrect: false },
      { text: "NOI", rationale: "NOI means net operating income, which is a valuation and investment concept.", isCorrect: false },
      { text: "LTV", rationale: "LTV means loan-to-value ratio, not a payment breakdown.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 13,
    question: "A valid contract for the sale of real estate generally requires:",
    answerOptions: [
      { text: "Offer, acceptance, consideration, lawful objective, and competent parties", rationale: "These are the standard tested elements of a valid contract.", isCorrect: true },
      { text: "Only a verbal promise and a handshake", rationale: "Real estate contracts are commonly subject to the Statute of Frauds and generally must be in writing to be enforceable.", isCorrect: false },
      { text: "A title insurance policy before formation", rationale: "Title insurance is not an element of contract formation.", isCorrect: false },
      { text: "Recording before the parties sign", rationale: "Recording is not part of creating a valid sales contract.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 14,
    question: "A contract that lacks legal effect from the beginning is:",
    answerOptions: [
      { text: "Void", rationale: "A void contract has no legal effect from inception, often because of illegality or a missing required element.", isCorrect: true },
      { text: "Voidable", rationale: "A voidable contract is valid unless the protected party chooses to rescind it.", isCorrect: false },
      { text: "Enforceable", rationale: "An enforceable contract is the opposite of void.", isCorrect: false },
      { text: "Executed", rationale: "Executed refers to performance or completion, not lack of validity.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 15,
    question: "A broker representing both buyer and seller in the same transaction is engaged in:",
    answerOptions: [
      { text: "Dual agency", rationale: "Dual agency exists when one brokerage or agent represents both sides of the same transaction, subject to state law and required consent.", isCorrect: true },
      { text: "Single agency", rationale: "Single agency means representation of only one side.", isCorrect: false },
      { text: "Subagency", rationale: "Subagency involves delegated agency relationships, not necessarily both sides of one transaction.", isCorrect: false },
      { text: "Severalty", rationale: "Severalty is an ownership concept, not an agency relationship.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 16,
    question: "Which of the following is a common fiduciary duty owed by an agent to a client?",
    answerOptions: [
      { text: "Confidentiality", rationale: "Confidentiality is a classic fiduciary duty owed to the client, along with loyalty, obedience, disclosure, reasonable care, and accounting.", isCorrect: true },
      { text: "Silence about all defects", rationale: "An agent must not conceal known material facts.", isCorrect: false },
      { text: "Refusal to account for funds", rationale: "Accounting is a fiduciary duty, so refusal would violate the duty.", isCorrect: false },
      { text: "Automatic acceptance of any offer", rationale: "Agents present offers; they do not automatically accept them on the client's behalf unless specifically authorized.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 17,
    question: "Under the federal Fair Housing Act, which of the following is a protected class?",
    answerOptions: [
      { text: "Familial status", rationale: "Familial status is one of the seven core federally protected classes under the Fair Housing Act.", isCorrect: true },
      { text: "Age in all cases", rationale: "Age is not one of the core federal Fair Housing Act protected classes in the general way stated here.", isCorrect: false },
      { text: "Marital status in all states", rationale: "Marital status may be protected under some state laws, but it is not one of the seven core federal classes.", isCorrect: false },
      { text: "Political affiliation", rationale: "Political affiliation is not one of the core federal Fair Housing Act protected classes.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 18,
    question: "Lead-based paint disclosure requirements are generally associated with residential property built before:",
    answerOptions: [
      { text: "1978", rationale: "The federal lead-based paint disclosure rules are generally tied to many residential properties built before 1978.", isCorrect: true },
      { text: "1965", rationale: "That is not the key cutoff used for the standard federal disclosure rule.", isCorrect: false },
      { text: "1985", rationale: "That is later than the standard federal threshold.", isCorrect: false },
      { text: "1992", rationale: "That is not the pre-1978 lead-based paint disclosure cutoff.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 19,
    question: "The primary purpose of recording a deed is to:",
    answerOptions: [
      { text: "Give public notice and help protect priority", rationale: "Recording helps provide constructive notice to the world and can protect the grantee against later claims under recording statutes.", isCorrect: true },
      { text: "Create the contract of sale", rationale: "The contract of sale is formed by agreement, not by recording the deed.", isCorrect: false },
      { text: "Guarantee financing approval", rationale: "Recording does not control loan underwriting approval.", isCorrect: false },
      { text: "Replace title insurance", rationale: "Recording and title insurance serve different functions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 20,
    question: "If annual property taxes are $6,000, what is the monthly tax amount for escrow purposes?",
    answerOptions: [
      { text: "$500", rationale: "To find the monthly tax amount, divide the annual taxes by 12. $6,000 / 12 = $500.", isCorrect: true },
      { text: "$600", rationale: "That would overstate the monthly amount.", isCorrect: false },
      { text: "$720", rationale: "That does not come from dividing $6,000 by 12.", isCorrect: false },
      { text: "$5,000", rationale: "That is close to the annual amount, not the monthly amount.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 21,
    question: "Which statement best describes a fee simple determinable estate?",
    answerOptions: [
      { text: "It automatically ends if a stated condition occurs", rationale: "A fee simple determinable is a defeasible fee that terminates automatically upon the happening of the stated event or condition.", isCorrect: true },
      { text: "It lasts only for the tenant's lifetime", rationale: "That describes a life estate, not a fee simple determinable.", isCorrect: false },
      { text: "It gives possession for a fixed lease term", rationale: "That describes an estate for years, which is a leasehold.", isCorrect: false },
      { text: "It can exist only between spouses", rationale: "That is unrelated to defeasible fee estates.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 22,
    question: "Which future interest follows a life estate when title is set to pass to a named third party?",
    answerOptions: [
      { text: "Remainder", rationale: "A remainder is the future interest that goes to a third party after the prior estate ends. A reversion goes back to the grantor.", isCorrect: true },
      { text: "Reversion", rationale: "A reversion returns the property to the grantor, not to a named third party.", isCorrect: false },
      { text: "Easement", rationale: "An easement is a use right, not a future ownership interest.", isCorrect: false },
      { text: "Lien", rationale: "A lien is a security interest, not a future estate.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 23,
    question: "A tenant in common who dies normally passes that interest to:",
    answerOptions: [
      { text: "The tenant's heirs or devisees", rationale: "Tenancy in common does not include a right of survivorship, so the deceased owner's share generally passes through the estate.", isCorrect: true },
      { text: "The surviving co-owners automatically", rationale: "That would describe survivorship, which is typical of joint tenancy rather than tenancy in common.", isCorrect: false },
      { text: "The state through escheat immediately", rationale: "Escheat applies only in narrow no-heir circumstances and is not the normal result.", isCorrect: false },
      { text: "The broker who listed the property", rationale: "A listing broker does not inherit a tenant in common's interest.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 24,
    question: "An easement created by long, open, notorious, continuous, and adverse use for the statutory period is an easement by:",
    answerOptions: [
      { text: "Prescription", rationale: "A prescriptive easement arises through adverse use over time, similar in concept to adverse possession except that it creates use rights rather than ownership.", isCorrect: true },
      { text: "Necessity", rationale: "An easement by necessity is based on landlocked access needs, not adverse use over time.", isCorrect: false },
      { text: "Condemnation", rationale: "Easements by condemnation are created through government action.", isCorrect: false },
      { text: "Release", rationale: "A release terminates an easement rather than creating one.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 25,
    question: "A property owner who loses land gradually because of water action is experiencing:",
    answerOptions: [
      { text: "Erosion", rationale: "Erosion is the gradual wearing away of land by water or wind, while accretion is the gradual buildup of land.", isCorrect: true },
      { text: "Accretion", rationale: "Accretion is the gradual addition of land, not loss of land.", isCorrect: false },
      { text: "Avulsion", rationale: "Avulsion is a sudden, noticeable land change rather than a gradual one.", isCorrect: false },
      { text: "Condemnation", rationale: "Condemnation is a legal eminent-domain process, not a natural land-change process.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 26,
    question: "A zoning change that reduces permitted density is commonly called:",
    answerOptions: [
      { text: "Downzoning", rationale: "Downzoning reduces intensity or density of allowed use. It is different from a variance, which grants relief without changing the zoning classification.", isCorrect: true },
      { text: "Variance", rationale: "A variance is relief from the ordinance, not a reclassification that lowers permitted density.", isCorrect: false },
      { text: "Accretion", rationale: "Accretion is a natural land-change concept.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat concerns state succession to property when there are no heirs.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 27,
    question: "The legal process used to carry out eminent domain is called:",
    answerOptions: [
      { text: "Condemnation", rationale: "Eminent domain is the power; condemnation is the process by which the government exercises that power.", isCorrect: true },
      { text: "Foreclosure", rationale: "Foreclosure enforces liens, not public taking for public use.", isCorrect: false },
      { text: "Probate", rationale: "Probate handles estate administration after death.", isCorrect: false },
      { text: "Subrogation", rationale: "Subrogation is an insurance-related concept, not a land-taking process.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 28,
    question: "A local law dividing a municipality into use districts is a:",
    answerOptions: [
      { text: "Zoning ordinance", rationale: "A zoning ordinance is the actual law that establishes districts and regulates land use, setbacks, density, and similar issues.", isCorrect: true },
      { text: "Special assessment", rationale: "A special assessment is a charge for a public improvement, not a use-control law.", isCorrect: false },
      { text: "Deed restriction", rationale: "A deed restriction is a private limitation, not a municipal ordinance.", isCorrect: false },
      { text: "Title covenant", rationale: "A title covenant is a promise in a deed, not a land-use regulation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 29,
    question: "The appraisal principle that a buyer will not pay more for a property than the cost of an equally desirable substitute is:",
    answerOptions: [
      { text: "Substitution", rationale: "The principle of substitution underlies market behavior and helps explain the sales comparison approach.", isCorrect: true },
      { text: "Regression", rationale: "Regression concerns value loss caused by surrounding lower-quality properties.", isCorrect: false },
      { text: "Conformity", rationale: "Conformity concerns a property's similarity to surrounding properties.", isCorrect: false },
      { text: "Contribution", rationale: "Contribution concerns how much a component adds to overall value.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 30,
    question: "A higher-quality home losing value because it is surrounded by lower-quality homes illustrates:",
    answerOptions: [
      { text: "Regression", rationale: "Regression occurs when a superior property suffers value loss from inferior surrounding properties.", isCorrect: true },
      { text: "Progression", rationale: "Progression is the opposite effect, where a lesser property gains value from better surroundings.", isCorrect: false },
      { text: "Substitution", rationale: "Substitution is about market alternatives, not neighborhood quality influence.", isCorrect: false },
      { text: "Accretion", rationale: "Accretion is a water-related land-change concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 31,
    question: "A borrower who pledges real estate as security for a loan while retaining possession is engaging in:",
    answerOptions: [
      { text: "Hypothecation", rationale: "Hypothecation means the borrower uses the property as collateral without surrendering possession of it.", isCorrect: true },
      { text: "Subrogation", rationale: "Subrogation is an insurance concept involving substitution of one party's rights for another's.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat is a government succession power.", isCorrect: false },
      { text: "Accession", rationale: "Accession concerns acquiring title through annexation or addition, not pledging collateral.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 32,
    question: "A loan in which the borrower pays interest periodically and the principal is due in one lump sum at maturity is a:",
    answerOptions: [
      { text: "Straight note", rationale: "A straight note generally requires periodic interest payments with the principal due at maturity rather than gradually amortized.", isCorrect: true },
      { text: "Fully amortized loan", rationale: "A fully amortized loan pays down principal over time until the balance reaches zero at maturity.", isCorrect: false },
      { text: "Graduated payment loan", rationale: "Graduated payment loans change payment amounts over time and are not defined by interest-only payments with principal due at the end.", isCorrect: false },
      { text: "Package deed", rationale: "That is not a standard mortgage-loan type.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 33,
    question: "A contract that is valid unless one party with the legal right chooses to disaffirm it is:",
    answerOptions: [
      { text: "Voidable", rationale: "A voidable contract remains valid until the protected party elects to rescind it.", isCorrect: true },
      { text: "Void", rationale: "A void contract has no legal effect from the start.", isCorrect: false },
      { text: "Unenforceable in all cases", rationale: "Voidable does not mean automatically unenforceable in every circumstance.", isCorrect: false },
      { text: "Executed by law", rationale: "That is not the correct contract-status concept here.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 34,
    question: "A court order requiring a party to perform a real estate contract as agreed is:",
    answerOptions: [
      { text: "Specific performance", rationale: "Because real estate is considered unique, specific performance is a classic remedy for breach of a real estate sales contract.", isCorrect: true },
      { text: "Accord and satisfaction", rationale: "That concerns settlement of an obligation by substituted performance.", isCorrect: false },
      { text: "Rescission", rationale: "Rescission cancels the contract rather than compelling performance.", isCorrect: false },
      { text: "Commingling", rationale: "Commingling concerns improper mixing of funds, not contract remedies.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 35,
    question: "An agent whose authority is limited to a specific transaction is usually a:",
    answerOptions: [
      { text: "Special agent", rationale: "Real estate brokers are commonly treated as special agents because their authority is limited to acts connected with a particular transaction.", isCorrect: true },
      { text: "Universal agent", rationale: "A universal agent has much broader authority than a typical real estate broker.", isCorrect: false },
      { text: "General partner", rationale: "That is a business-entity role, not the agency classification being tested.", isCorrect: false },
      { text: "Principal", rationale: "The principal is the party represented, not the agent.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 36,
    question: "Which action is most likely to create implied agency?",
    answerOptions: [
      { text: "Acting in a way that causes a consumer reasonably to believe representation exists", rationale: "Implied agency arises from conduct rather than express written or oral agreement, which makes careless behavior risky.", isCorrect: true },
      { text: "Filing a deed in the county records", rationale: "Recording title documents does not create an agency relationship.", isCorrect: false },
      { text: "Paying property taxes on time", rationale: "Tax payment has nothing to do with implying agency.", isCorrect: false },
      { text: "Ordering a home inspection after closing", rationale: "That action does not by itself create agency representation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 37,
    question: "Which of the following would most likely violate the federal Fair Housing Act?",
    answerOptions: [
      { text: "Advertising that a property is perfect for Christians only", rationale: "Advertising that indicates a religious preference can violate fair housing law even before a transaction is completed.", isCorrect: true },
      { text: "Requiring all applicants to meet the same objective income standard", rationale: "Applying a neutral, consistent standard is not inherently discriminatory as stated.", isCorrect: false },
      { text: "Refusing to discuss a buyer's religion during a showing", rationale: "Avoiding religion-based discussion is not a fair-housing violation.", isCorrect: false },
      { text: "Providing a buyer with a list of all homes meeting the buyer's price range", rationale: "Giving objective listings based on lawful criteria is not discriminatory.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 38,
    question: "A known hidden defect that could affect a reasonable buyer's decision is best described as:",
    answerOptions: [
      { text: "A material latent defect", rationale: "A latent defect is hidden, and if it is material, it is significant enough that a reasonable buyer would care about it.", isCorrect: true },
      { text: "A trade fixture", rationale: "A trade fixture is a personal property item used in business, not a hidden defect.", isCorrect: false },
      { text: "A point of beginning", rationale: "That is a legal-description term, not a property-condition issue.", isCorrect: false },
      { text: "A deed covenant", rationale: "A deed covenant is a title promise, not a condition defect.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 39,
    question: "The primary purpose of title insurance is to:",
    answerOptions: [
      { text: "Protect against certain past title defects not discovered before closing", rationale: "Title insurance addresses covered defects in title arising from events that already occurred before the policy was issued.", isCorrect: true },
      { text: "Insure the property against future fire loss", rationale: "That is hazard or homeowners insurance, not title insurance.", isCorrect: false },
      { text: "Guarantee the borrower will repay the mortgage", rationale: "Title insurance does not insure repayment performance.", isCorrect: false },
      { text: "Establish the appraised value of the property", rationale: "An appraisal estimates value; title insurance does not.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 40,
    question: "If a property sells for $420,000 and the total commission is 6%, what is the total commission?",
    answerOptions: [
      { text: "$25,200", rationale: "Multiply the sale price by the commission rate. $420,000 x 0.06 = $25,200.", isCorrect: true },
      { text: "$24,000", rationale: "That would reflect the wrong percentage or wrong multiplication.", isCorrect: false },
      { text: "$42,000", rationale: "That would equal 10 percent, not 6 percent.", isCorrect: false },
      { text: "$2,520", rationale: "That is too small and reflects a decimal-place error.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 41,
    question: "A grantor who gives a life estate to another person but keeps the future interest has retained a:",
    answerOptions: [
      { text: "Reversion", rationale: "A reversion is the future interest retained by the grantor when the property is to come back after the lesser estate ends.", isCorrect: true },
      { text: "Remainder", rationale: "A remainder goes to a third party, not back to the grantor.", isCorrect: false },
      { text: "Leasehold", rationale: "A leasehold is a possessory estate, not a retained future interest.", isCorrect: false },
      { text: "Lien release", rationale: "A lien release concerns debt security, not future ownership.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 42,
    question: "Which legal description method is generally most suitable for an irregularly shaped parcel?",
    answerOptions: [
      { text: "Metes and bounds", rationale: "Metes and bounds is commonly used for irregular parcels because it traces the boundaries by direction and distance from the point of beginning.", isCorrect: true },
      { text: "Lot and block only", rationale: "Lot and block is commonly used in recorded subdivisions rather than as the standard answer for irregular standalone parcels.", isCorrect: false },
      { text: "PITI description", rationale: "PITI is a financing acronym, not a legal description.", isCorrect: false },
      { text: "Cap-rate description", rationale: "Capitalization rate is a valuation concept, not a legal-description method.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 43,
    question: "A transfer of a tenant's entire remaining leasehold interest to another party is usually a:",
    answerOptions: [
      { text: "Lease assignment", rationale: "An assignment transfers the tenant's full remaining interest, while a sublease transfers less than the entire remaining term.", isCorrect: true },
      { text: "Sublease", rationale: "A sublease occurs when the original tenant keeps some reversionary interest rather than transferring the entire remaining term.", isCorrect: false },
      { text: "Life estate", rationale: "A life estate is a freehold interest, not a lease transfer.", isCorrect: false },
      { text: "Variance", rationale: "A variance is zoning relief, not a leasehold transfer.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 44,
    question: "A lender's voluntary agreement to let another lien move ahead in priority is called:",
    answerOptions: [
      { text: "Subordination", rationale: "Subordination changes the normal order of lien priority by agreement.", isCorrect: true },
      { text: "Novation", rationale: "Novation substitutes a new obligation or party, not lien priority.", isCorrect: false },
      { text: "Accession", rationale: "Accession concerns additions or annexation to property, not lien ranking.", isCorrect: false },
      { text: "Severance", rationale: "Severance relates to splitting interests, not voluntarily changing priority.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 45,
    question: "Which of the following best describes a package mortgage?",
    answerOptions: [
      { text: "A loan covering both real property and personal property", rationale: "A package mortgage secures both the real estate and certain personal property in one financing arrangement.", isCorrect: true },
      { text: "A mortgage paid in one monthly installment only", rationale: "That does not define a package mortgage.", isCorrect: false },
      { text: "A deed restriction on subdivision lots", rationale: "A deed restriction is a land-use limitation, not a mortgage type.", isCorrect: false },
      { text: "A tax assessment for public improvements", rationale: "That describes a special assessment, not a loan.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 46,
    question: "A borrower who takes title 'subject to' an existing mortgage is usually:",
    answerOptions: [
      { text: "Taking title without formally assuming personal liability for the debt", rationale: "When title is taken subject to a mortgage, the debt remains on the property, but the buyer does not formally assume personal liability in the usual sense.", isCorrect: true },
      { text: "Automatically released from all risk of foreclosure", rationale: "The property can still be foreclosed if the debt is not paid.", isCorrect: false },
      { text: "Guaranteed the right to a lower interest rate", rationale: "Taking title subject to a loan does not guarantee a change in rate.", isCorrect: false },
      { text: "Converting the mortgage into title insurance", rationale: "Those are entirely different legal and financial concepts.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 47,
    question: "A contract that has been fully performed by all parties is:",
    answerOptions: [
      { text: "Executed", rationale: "An executed contract is one in which all parties have completed their contractual obligations.", isCorrect: true },
      { text: "Executory", rationale: "An executory contract still has obligations remaining to be performed.", isCorrect: false },
      { text: "Voidable", rationale: "Voidable describes a contract that may be rescinded by a protected party.", isCorrect: false },
      { text: "Unilateral only", rationale: "Unilateral concerns promise structure, not whether the contract has been fully performed.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 48,
    question: "An option contract gives the optionee:",
    answerOptions: [
      { text: "The right, but not the obligation, to buy or lease under stated terms", rationale: "An option binds the optionor to keep the offer open, but the optionee may choose whether to exercise it.", isCorrect: true },
      { text: "An immediate deed at no cost", rationale: "An option does not itself transfer title automatically.", isCorrect: false },
      { text: "Automatic lender approval", rationale: "Financing approval is not created by an option contract.", isCorrect: false },
      { text: "A guaranteed title insurance policy", rationale: "Title insurance is separate from the option right.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 49,
    question: "Which statement best describes a customer's relationship to a licensee?",
    answerOptions: [
      { text: "The customer is owed honest and fair dealing, but not full fiduciary representation as a client", rationale: "The exam often distinguishes a client from a customer. A customer is not represented as a client but is still owed lawful and honest conduct.", isCorrect: true },
      { text: "The customer automatically receives confidentiality, loyalty, and obedience in every case", rationale: "Those are hallmark fiduciary duties owed to clients, not automatically to customers.", isCorrect: false },
      { text: "The customer becomes a principal in the brokerage", rationale: "The customer does not become the principal merely by dealing with the licensee.", isCorrect: false },
      { text: "The customer can require the broker to ignore material defects", rationale: "A licensee cannot lawfully conceal known material facts.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 50,
    question: "A principal who later approves an unauthorized act of an agent is engaging in:",
    answerOptions: [
      { text: "Ratification", rationale: "Ratification occurs when the principal accepts and approves an act done on the principal's behalf without authority at the time it was done.", isCorrect: true },
      { text: "Subrogation", rationale: "Subrogation is an insurance concept, not an agency approval concept.", isCorrect: false },
      { text: "Foreclosure", rationale: "Foreclosure enforces a lien, not an agency relationship.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat is a government succession power.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 51,
    question: "Which of the following best illustrates illegal steering?",
    answerOptions: [
      { text: "Guiding families with children away from a building because children would not fit in", rationale: "Steering occurs when a licensee channels people toward or away from housing based on protected characteristics rather than lawful, objective criteria.", isCorrect: true },
      { text: "Showing all homes that meet a buyer's requested price and location criteria", rationale: "That is objective client service rather than steering.", isCorrect: false },
      { text: "Refusing to discuss a buyer's religion during showings", rationale: "Avoiding religion-based guidance is not steering.", isCorrect: false },
      { text: "Explaining school-district boundaries from official public sources", rationale: "Providing objective public-information sources is not the same as steering.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 52,
    question: "Refusing to permit a reasonable accommodation for a person with a disability may violate:",
    answerOptions: [
      { text: "The federal Fair Housing Act", rationale: "Reasonable accommodation questions are a classic fair-housing topic involving equal housing opportunity for persons with disabilities.", isCorrect: true },
      { text: "Only the Internal Revenue Code", rationale: "Tax law is not the core legal framework for accommodation rights in housing.", isCorrect: false },
      { text: "Only local tax-assessment rules", rationale: "Assessment rules do not govern disability accommodations in housing.", isCorrect: false },
      { text: "Only deed-covenant law", rationale: "Private covenants are not the primary source of this fair-housing obligation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 53,
    question: "A hidden condition known to the seller that materially affects value or safety is best described as:",
    answerOptions: [
      { text: "A latent material defect", rationale: "Latent means hidden, and material means significant enough that a reasonable person would care about it.", isCorrect: true },
      { text: "A point of beginning", rationale: "That is a metes-and-bounds surveying term.", isCorrect: false },
      { text: "An estate in severalty", rationale: "Severalty means sole ownership, not a defect.", isCorrect: false },
      { text: "A servient tenement", rationale: "That is the parcel burdened by an easement appurtenant.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 54,
    question: "Which statement about stigmatized property is most accurate on a national exam?",
    answerOptions: [
      { text: "Disclosure duties may vary by state because stigma issues are not uniformly treated nationwide", rationale: "National exam questions often test that not every nonphysical or psychological issue has the same disclosure rule in every jurisdiction.", isCorrect: true },
      { text: "Federal law requires disclosure of every stigmatizing event in every state", rationale: "That overstates the law and ignores state variation.", isCorrect: false },
      { text: "Stigmatized property can never affect market value", rationale: "Market perception may affect value even if the condition is not physical.", isCorrect: false },
      { text: "Stigma automatically becomes a title defect", rationale: "A stigma issue is not automatically a title defect.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 55,
    question: "A deed that conveys whatever interest the grantor may have, without title warranties, is a:",
    answerOptions: [
      { text: "Quitclaim deed", rationale: "A quitclaim deed transfers the grantor's interest, if any, without warranties of title.", isCorrect: true },
      { text: "General warranty deed", rationale: "A general warranty deed gives broad title covenants, which a quitclaim deed does not.", isCorrect: false },
      { text: "Special warranty deed", rationale: "A special warranty deed still gives limited title warranties.", isCorrect: false },
      { text: "Mortgage note", rationale: "A note is a debt instrument, not a deed.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 56,
    question: "A title defect created by a forged deed is generally serious because:",
    answerOptions: [
      { text: "A forged deed is generally ineffective to transfer valid title", rationale: "Forgery creates a major defect in the chain of title because the purported conveyance is not legally effective in the usual sense.", isCorrect: true },
      { text: "A forged deed automatically increases market value", rationale: "Forgery creates title risk, not added value.", isCorrect: false },
      { text: "Forgery is cured simply by paying transfer tax", rationale: "Paying taxes does not cure a forged conveyance.", isCorrect: false },
      { text: "Forgery converts the deed into a lease", rationale: "Forgery does not change the instrument into a lease.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 57,
    question: "If annual taxes are $7,200 and the closing uses a 360-day year, the daily tax amount is:",
    answerOptions: [
      { text: "$20", rationale: "Divide the annual tax by 360. $7,200 / 360 = $20 per day.", isCorrect: true },
      { text: "$24", rationale: "That would overstate the daily amount.", isCorrect: false },
      { text: "$18", rationale: "That does not come from dividing $7,200 by 360.", isCorrect: false },
      { text: "$200", rationale: "That is too high by a factor of 10.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 58,
    question: "A quarter section of land contains:",
    answerOptions: [
      { text: "160 acres", rationale: "A full section contains 640 acres, so one quarter section contains 160 acres.", isCorrect: true },
      { text: "40 acres", rationale: "Forty acres would be one quarter of a quarter section.", isCorrect: false },
      { text: "320 acres", rationale: "That would be half a section, not a quarter section.", isCorrect: false },
      { text: "640 acres", rationale: "That is a full section.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 59,
    question: "A GRM is found by dividing:",
    answerOptions: [
      { text: "Sale price by gross rent", rationale: "Gross rent multiplier is calculated by dividing sale price by gross rent, making it a quick screening tool for certain income properties.", isCorrect: true },
      { text: "Gross rent by sale price", rationale: "That reverses the formula.", isCorrect: false },
      { text: "NOI by capitalization rate", rationale: "That formula is used to estimate value from income, not GRM.", isCorrect: false },
      { text: "Sale price by assessed value", rationale: "That ratio is unrelated to gross rent multiplier.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 60,
    question: "A settlement statement debit to the buyer generally means:",
    answerOptions: [
      { text: "An amount charged to the buyer", rationale: "A debit is an amount charged against a party on the settlement statement and usually increases the buyer's required funds.", isCorrect: true },
      { text: "An amount paid to the seller's lender only", rationale: "A debit can represent many kinds of charges and is not limited to the seller's lender.", isCorrect: false },
      { text: "A reduction in purchase price by law", rationale: "A debit is a charge, not an automatic legal reduction.", isCorrect: false },
      { text: "A waiver of the buyer's closing costs", rationale: "A debit is the opposite of a waiver or credit.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 61,
    question: "Which of the following best describes severalty?",
    answerOptions: [
      { text: "Ownership by one person or one legal entity alone", rationale: "Severalty means sole ownership, despite the name sounding like multiple ownership.", isCorrect: true },
      { text: "Ownership by spouses with survivorship only", rationale: "That points more toward tenancy by the entirety, not severalty.", isCorrect: false },
      { text: "A month-to-month tenancy", rationale: "That is a leasehold arrangement, not a form of title ownership.", isCorrect: false },
      { text: "A broker representing both parties", rationale: "That describes dual agency, not ownership in severalty.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 62,
    question: "A co-owner's legal right to use and enjoy the entire property rather than a physically separate section is called:",
    answerOptions: [
      { text: "Undivided interest", rationale: "An undivided interest means the owner holds a share in the whole property, not ownership of a specific room or section.", isCorrect: true },
      { text: "Avulsion", rationale: "Avulsion is a sudden land change caused by water action.", isCorrect: false },
      { text: "Severalty", rationale: "Severalty means sole ownership rather than shared ownership of the whole parcel.", isCorrect: false },
      { text: "Subordination", rationale: "Subordination changes lien priority, not the nature of co-ownership.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 63,
    question: "Which of the following best describes a restrictive covenant?",
    answerOptions: [
      { text: "A private limitation on land use", rationale: "Restrictive covenants are private controls that limit how land may be used or improved.", isCorrect: true },
      { text: "A tax charge for public improvements", rationale: "That describes a special assessment, not a restrictive covenant.", isCorrect: false },
      { text: "A lender's right to accelerate a loan", rationale: "That describes an acceleration clause in financing.", isCorrect: false },
      { text: "A federal insurance endorsement", rationale: "Restrictive covenants are not insurance instruments.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 64,
    question: "A township in the rectangular survey system contains:",
    answerOptions: [
      { text: "36 sections", rationale: "A township is generally six miles by six miles and contains 36 one-mile-square sections.", isCorrect: true },
      { text: "6 sections", rationale: "A township contains 36 sections, not 6.", isCorrect: false },
      { text: "160 acres", rationale: "That is the size of a quarter section, not a township.", isCorrect: false },
      { text: "640 acres", rationale: "That is the size of a full section, not a township.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 65,
    question: "A seller financing arrangement in which the seller accepts a mortgage from the buyer as part of the purchase price is:",
    answerOptions: [
      { text: "A purchase-money mortgage", rationale: "A purchase-money mortgage is seller financing in which the buyer gives the seller a mortgage as part of the purchase transaction.", isCorrect: true },
      { text: "A package deed", rationale: "That is not the correct financing term.", isCorrect: false },
      { text: "An easement in gross", rationale: "That is a nonpossessory use interest, not a financing arrangement.", isCorrect: false },
      { text: "A deed restriction", rationale: "A deed restriction limits use; it does not finance the purchase.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 66,
    question: "Which financing term means a lender may demand immediate full payment if the borrower defaults?",
    answerOptions: [
      { text: "Acceleration clause", rationale: "An acceleration clause lets the lender call the full unpaid balance due upon default.", isCorrect: true },
      { text: "Habendum clause", rationale: "A habendum clause appears in deeds and describes the interest conveyed.", isCorrect: false },
      { text: "Quiet title clause", rationale: "Quiet title is a lawsuit concept, not a mortgage-payment clause.", isCorrect: false },
      { text: "Contribution principle", rationale: "Contribution is an appraisal principle, not a loan clause.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 67,
    question: "A contract term requiring certain conditions to be satisfied before a sale closes is a:",
    answerOptions: [
      { text: "Contingency", rationale: "Contingencies make the contract dependent on stated events such as financing, inspection, or appraisal.", isCorrect: true },
      { text: "Covenant deed", rationale: "That is not the contract concept for a condition that must be met.", isCorrect: false },
      { text: "Subordination", rationale: "Subordination is a lien-priority concept.", isCorrect: false },
      { text: "Metes call", rationale: "That relates to legal descriptions, not contract conditions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 68,
    question: "A court action to establish clear ownership against adverse claims is a:",
    answerOptions: [
      { text: "Quiet title action", rationale: "Quiet title litigation is used to resolve title disputes and clear defects or adverse claims.", isCorrect: true },
      { text: "Specific performance action", rationale: "Specific performance compels contract performance; it does not primarily resolve adverse title claims.", isCorrect: false },
      { text: "Variance petition", rationale: "A variance petition seeks zoning relief.", isCorrect: false },
      { text: "Sublease action", rationale: "Subleasing concerns leasehold transfer, not title adjudication.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 69,
    question: "A principal's written authorization allowing an agent to act in broad matters is commonly a:",
    answerOptions: [
      { text: "Power of attorney", rationale: "A power of attorney is a written instrument granting authority to act for another person, sometimes broadly enough to create a universal-agency situation.", isCorrect: true },
      { text: "Certificate of occupancy", rationale: "That concerns building-use approval, not agency authority.", isCorrect: false },
      { text: "Mechanic's lien", rationale: "That is a lien for unpaid labor or materials.", isCorrect: false },
      { text: "Property condition disclosure", rationale: "That is a seller-condition document, not an agency authorization.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 70,
    question: "A principal who causes a third party to reasonably believe an agent has authority may create:",
    answerOptions: [
      { text: "Apparent authority", rationale: "Apparent authority arises from appearances to third parties and can bind the principal even if the agent lacked actual authority.", isCorrect: true },
      { text: "Escheat", rationale: "Escheat is a government property-succession concept.", isCorrect: false },
      { text: "Accretion", rationale: "Accretion is a gradual buildup of land by natural causes.", isCorrect: false },
      { text: "Conformity", rationale: "Conformity is an appraisal principle.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 71,
    question: "An ad stating 'ideal for a young married couple with no children' most likely violates:",
    answerOptions: [
      { text: "Fair housing advertising rules", rationale: "That statement expresses preferences connected to protected-status concerns and can violate fair housing advertising rules.", isCorrect: true },
      { text: "Only tax-assessment laws", rationale: "Tax assessment rules are unrelated to discriminatory advertising content.", isCorrect: false },
      { text: "Only title insurance regulations", rationale: "Title insurance does not govern the wording of housing ads.", isCorrect: false },
      { text: "Only local deed recording rules", rationale: "Recording rules do not control fair housing ad compliance.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 72,
    question: "Which federal law is most associated with disclosure of many pre-1978 residential lead-based paint hazards?",
    answerOptions: [
      { text: "Lead-Based Paint Hazard Reduction Act", rationale: "Lead-based paint questions are often anchored to the federal disclosure rules for many pre-1978 residential properties.", isCorrect: true },
      { text: "Sherman Antitrust Act", rationale: "That law concerns antitrust, not lead-based paint disclosure.", isCorrect: false },
      { text: "Megan's Law", rationale: "That law is unrelated to standard lead-paint disclosure obligations.", isCorrect: false },
      { text: "Interstate Commerce Act", rationale: "That is not the primary federal lead-based paint disclosure law.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 73,
    question: "A hidden environmental condition known to the seller that could affect a reasonable buyer's decision is most likely:",
    answerOptions: [
      { text: "A material latent defect", rationale: "A hidden known environmental problem may be both latent and material if it affects value, safety, or desirability.", isCorrect: true },
      { text: "A deed covenant of warranty", rationale: "Deed covenants concern title promises, not hidden condition problems.", isCorrect: false },
      { text: "A township-range descriptor", rationale: "That is a legal-description concept.", isCorrect: false },
      { text: "An executed contract", rationale: "That describes a fully performed contract, not a defect.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 74,
    question: "A lender's title insurance policy primarily protects:",
    answerOptions: [
      { text: "The lender's lien position", rationale: "A lender's policy is designed to protect the lender's secured interest in the property, while an owner's policy protects the owner.", isCorrect: true },
      { text: "The buyer's furniture", rationale: "Title insurance does not insure personal property furniture.", isCorrect: false },
      { text: "The seller's tax return", rationale: "Tax filings are unrelated to title insurance coverage.", isCorrect: false },
      { text: "The appraiser's license", rationale: "Title insurance does not insure professional licensing.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 75,
    question: "The clause in a deed that states the type of estate or interest being conveyed is the:",
    answerOptions: [
      { text: "Habendum clause", rationale: "The habendum clause identifies what estate the grantee is to have and hold.", isCorrect: true },
      { text: "Acceleration clause", rationale: "That is a mortgage-default clause, not a deed clause describing the estate conveyed.", isCorrect: false },
      { text: "Defeasance clause", rationale: "Defeasance is a financing concept tied to release of the mortgage lien upon repayment.", isCorrect: false },
      { text: "Subordination clause", rationale: "Subordination concerns lien priority, not the estate conveyed in a deed.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 76,
    question: "If a property is assessed at $180,000 and the tax rate is 30 mills, the annual property tax is:",
    answerOptions: [
      { text: "$5,400", rationale: "A 30-mill rate means $30 per $1,000 of assessed value. $180,000 / 1,000 = 180, and 180 x $30 = $5,400.", isCorrect: true },
      { text: "$3,000", rationale: "That undercalculates the tax at the stated mill rate.", isCorrect: false },
      { text: "$54,000", rationale: "That is too high and reflects misuse of the formula.", isCorrect: false },
      { text: "$600", rationale: "That is far below the correct annual tax.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 77,
    question: "If a property's sale price is $360,000 and the buyer makes a 25% down payment, the loan amount is:",
    answerOptions: [
      { text: "$270,000", rationale: "A 25 percent down payment on $360,000 is $90,000. Subtracting that from the sale price leaves a loan amount of $270,000.", isCorrect: true },
      { text: "$90,000", rationale: "That is the down payment, not the loan amount.", isCorrect: false },
      { text: "$285,000", rationale: "That does not reflect a 25 percent down payment.", isCorrect: false },
      { text: "$300,000", rationale: "That would imply a smaller down payment than stated.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 78,
    question: "If a property rents for $2,200 per month, the annual gross rent is:",
    answerOptions: [
      { text: "$26,400", rationale: "Multiply monthly rent by 12. $2,200 x 12 = $26,400.", isCorrect: true },
      { text: "$24,200", rationale: "That does not result from multiplying $2,200 by 12.", isCorrect: false },
      { text: "$22,000", rationale: "That treats the monthly number incorrectly.", isCorrect: false },
      { text: "$18,400", rationale: "That understates the annual rent.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 79,
    question: "The borrower generally signs which document to promise repayment of the loan?",
    answerOptions: [
      { text: "Promissory note", rationale: "The promissory note is the debt instrument containing the borrower's promise to repay.", isCorrect: true },
      { text: "Lot-and-block plat", rationale: "That is a legal-description document, not a debt instrument.", isCorrect: false },
      { text: "Property survey only", rationale: "A survey shows boundaries, not the repayment promise.", isCorrect: false },
      { text: "Certificate of occupancy", rationale: "That concerns lawful building occupancy, not loan repayment.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 80,
    question: "A lease in which the tenant pays rent plus taxes, insurance, and maintenance is most commonly called:",
    answerOptions: [
      { text: "A net lease", rationale: "A net lease shifts some or all operating expenses to the tenant, unlike a gross lease where the landlord usually absorbs most of them.", isCorrect: true },
      { text: "A gross lease", rationale: "A gross lease usually means the landlord pays most or all operating expenses.", isCorrect: false },
      { text: "A life estate", rationale: "A life estate is an ownership estate, not a lease structure.", isCorrect: false },
      { text: "A tenancy at sufferance", rationale: "That is a holdover tenancy classification, not an expense-allocation lease type.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 81,
    question: "A landlord refuses to rent to a prospect because the prospect uses a wheelchair. Under federal law, this is most likely:",
    answerOptions: [
      { text: "Illegal disability discrimination", rationale: "Refusing to rent because of a disability is a core fair housing violation unless a specific legal exemption applies, and this fact pattern points to unlawful discrimination.", isCorrect: true },
      { text: "Permissible if the rent is reduced", rationale: "A lower rent does not cure discriminatory refusal to rent.", isCorrect: false },
      { text: "Allowed if the building is owner-occupied", rationale: "Owner occupancy does not create a blanket right to discriminate on disability in this way.", isCorrect: false },
      { text: "Only a zoning issue", rationale: "This concerns fair housing rights, not land-use regulation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 82,
    question: "A housing advertisement that says 'perfect for Christians' most directly violates:",
    answerOptions: [
      { text: "Fair housing advertising rules concerning religion", rationale: "Ads may not express a preference based on protected classes such as religion.", isCorrect: true },
      { text: "Truth-in-lending disclosure rules", rationale: "That law governs lending disclosures, not discriminatory ad language.", isCorrect: false },
      { text: "The deed recording act", rationale: "Recording laws do not regulate religious preferences in ads.", isCorrect: false },
      { text: "The lender's underwriting guidelines", rationale: "Underwriting is unrelated to the legality of housing advertising content.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 83,
    question: "Before a buyer is obligated under many federal lead-based paint rules for a covered residential property, the buyer must generally be given:",
    answerOptions: [
      { text: "A lead-based paint disclosure and informational pamphlet", rationale: "For many covered pre-1978 residential properties, federal law requires disclosure of known lead-based paint information and delivery of the EPA pamphlet before the buyer is bound.", isCorrect: true },
      { text: "A survey monument certification only", rationale: "Boundary information is not the lead-paint disclosure requirement.", isCorrect: false },
      { text: "A title insurance endorsement only", rationale: "Title insurance is separate from environmental-disclosure obligations.", isCorrect: false },
      { text: "A zoning variance application", rationale: "A variance application has nothing to do with lead-paint disclosure.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 84,
    question: "If a seller knows the basement floods after heavy rain and fails to reveal it, the issue is most likely:",
    answerOptions: [
      { text: "A material latent defect", rationale: "A hidden flooding condition can materially affect value or desirability and is the type of defect commonly tested as latent and material.", isCorrect: true },
      { text: "A deed restriction", rationale: "A deed restriction is a title or use limitation, not a hidden condition problem.", isCorrect: false },
      { text: "An emblement", rationale: "Emblements are annual crops, not property defects.", isCorrect: false },
      { text: "A general lien waiver", rationale: "A lien waiver is unrelated to undisclosed flooding conditions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 85,
    question: "Which deed usually gives the greatest warranties of title to the grantee?",
    answerOptions: [
      { text: "General warranty deed", rationale: "A general warranty deed provides the broadest commonly tested promises regarding title defects, including those arising before the grantor owned the property.", isCorrect: true },
      { text: "Quitclaim deed", rationale: "A quitclaim deed conveys whatever interest the grantor has, if any, with no warranties of title.", isCorrect: false },
      { text: "Bargain and sale deed without warranties", rationale: "This does not offer the broad warranty protection of a general warranty deed.", isCorrect: false },
      { text: "Tax deed", rationale: "A tax deed arises from a tax-sale process and is not known for broad title warranties.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 86,
    question: "The public recording system primarily exists to:",
    answerOptions: [
      { text: "Give notice of interests affecting title", rationale: "Recording laws are centered on providing public notice and helping establish priority among competing claims.", isCorrect: true },
      { text: "Set the property's market value", rationale: "Recording does not determine value.", isCorrect: false },
      { text: "Determine whether a borrower qualifies for a loan", rationale: "Loan qualification is an underwriting issue, not a recording-system purpose.", isCorrect: false },
      { text: "Calculate commission splits between brokers", rationale: "Broker compensation is unrelated to the core function of recording statutes.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 87,
    question: "In the sales comparison approach, an appraiser usually adjusts the comparable sale price when the comparable has:",
    answerOptions: [
      { text: "A feature difference from the subject property", rationale: "Comparable sales are adjusted for differences so the appraiser can better estimate what the subject would sell for in the market.", isCorrect: true },
      { text: "The same financing terms as the subject transaction", rationale: "If the relevant features are already comparable, that alone does not drive an adjustment.", isCorrect: false },
      { text: "The same bedroom count and lot size", rationale: "If those features are the same, they do not themselves require adjustment.", isCorrect: false },
      { text: "No meaningful difference from the subject", rationale: "No significant difference means no adjustment is needed for that factor.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 88,
    question: "Which appraisal principle is illustrated when a house loses value because a factory is built next door?",
    answerOptions: [
      { text: "External obsolescence", rationale: "External obsolescence is value loss caused by outside factors beyond the property boundaries, such as a nearby nuisance.", isCorrect: true },
      { text: "Plottage", rationale: "Plottage refers to the increased value created by combining parcels.", isCorrect: false },
      { text: "Assemblage", rationale: "Assemblage is the act of joining parcels under one ownership.", isCorrect: false },
      { text: "Contribution", rationale: "Contribution measures how much a feature adds to value, not value loss from outside influences.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 89,
    question: "Which government power is used when a county takes private land for a new public school and pays compensation?",
    answerOptions: [
      { text: "Eminent domain", rationale: "Eminent domain is the government's power to take private property for public use with just compensation.", isCorrect: true },
      { text: "Police power", rationale: "Police power regulates use for health, safety, and welfare but does not itself mean compensated taking.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat transfers property to the state when someone dies without heirs.", isCorrect: false },
      { text: "Taxation", rationale: "Taxation raises revenue; it is not the compensated taking power.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 90,
    question: "A zoning ordinance that limits a parcel to residential use is an example of:",
    answerOptions: [
      { text: "Police power over land use", rationale: "Zoning is a classic exercise of police power to promote public health, safety, and welfare through land-use regulation.", isCorrect: true },
      { text: "A deed covenant of seisin", rationale: "Seisin is a title warranty, not a land-use regulation.", isCorrect: false },
      { text: "The law of agency by ratification", rationale: "Ratification concerns authority in agency law, not zoning limits.", isCorrect: false },
      { text: "A title insurance endorsement", rationale: "An endorsement modifies title coverage; it does not create zoning restrictions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 91,
    question: "A buyer's broker who presents all offers promptly and keeps the buyer's motivations private is most directly showing duties of:",
    answerOptions: [
      { text: "Loyalty and confidentiality", rationale: "Prompt presentation of offers and protection of private negotiating information are classic fiduciary duties owed to a client.", isCorrect: true },
      { text: "Novation and assignment", rationale: "Those are contract concepts, not fiduciary duties.", isCorrect: false },
      { text: "Avulsion and accretion", rationale: "Those are land-change concepts, not agency duties.", isCorrect: false },
      { text: "Plottage and conformity", rationale: "Those are valuation or land concepts, not client-duty terms.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 92,
    question: "If a seller accepts a buyer's written offer and communicates that acceptance, the contract is generally:",
    answerOptions: [
      { text: "Executed", rationale: "Executed often means fully performed, not merely formed.", isCorrect: false },
      { text: "Voidable", rationale: "Voidable describes a contract that one party may rescind for a legal reason, not the normal effect of acceptance.", isCorrect: false },
      { text: "Unenforceable", rationale: "A properly formed accepted contract is not unenforceable just because it has been accepted.", isCorrect: false },
      { text: "Accepted and enforceable", rationale: "Once there is valid offer, acceptance, and communication, a binding contract generally exists if other essentials are met.", isCorrect: true }
    ],
    hint: ""
  },
  {
    questionNumber: 93,
    question: "When title to real property passes by a valid will after death, the transfer is by:",
    answerOptions: [
      { text: "Testate succession", rationale: "A person who dies with a valid will dies testate, and property passes according to the will.", isCorrect: true },
      { text: "Intestate succession", rationale: "Intestate succession applies when there is no valid will.", isCorrect: false },
      { text: "Accession", rationale: "Accession concerns additions to property, not inheritance by will.", isCorrect: false },
      { text: "Mortgage assumption", rationale: "Assumption is a financing concept, not a death-transfer classification.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 94,
    question: "A title search most directly aims to discover:",
    answerOptions: [
      { text: "Recorded defects and claims affecting ownership", rationale: "A title search examines the public record to find matters that could affect ownership or marketability of title.", isCorrect: true },
      { text: "Whether the home is decorated attractively", rationale: "Decor and staging are irrelevant to title search.", isCorrect: false },
      { text: "Whether the property will appraise above contract price", rationale: "Appraisal concerns value, not title defects.", isCorrect: false },
      { text: "How much commission the listing broker wants", rationale: "Broker compensation is not the purpose of title examination.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 95,
    question: "A rectangular survey description most commonly refers to land by:",
    answerOptions: [
      { text: "Township, range, and section", rationale: "The rectangular survey system describes land by principal meridians and base lines using townships, ranges, and sections.", isCorrect: true },
      { text: "Lot, block, and subdivision plat", rationale: "That describes the lot-and-block system, not rectangular survey.", isCorrect: false },
      { text: "Street address and ZIP code", rationale: "Mailing addresses are not the legal rectangular survey description.", isCorrect: false },
      { text: "Metes only, without direction or distance", rationale: "Metes and bounds uses directions and distances, and it is a separate legal-description method.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 96,
    question: "A tenant with a one-year lease who remains after expiration without permission is usually considered a:",
    answerOptions: [
      { text: "Tenant at sufferance", rationale: "A holdover tenant who remains without the landlord's consent is generally a tenant at sufferance.", isCorrect: true },
      { text: "Tenant in common", rationale: "That is a form of co-ownership, not a holdover tenancy.", isCorrect: false },
      { text: "Tenant for years", rationale: "That refers to the original lease term, not the status after expiration without consent.", isCorrect: false },
      { text: "Periodic tenant", rationale: "A periodic tenancy continues from period to period, usually with consent.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 97,
    question: "A buyer purchases a property for $480,000 and makes a 20% down payment. The loan amount is:",
    answerOptions: [
      { text: "$384,000", rationale: "A 20 percent down payment is $96,000. Subtracting that from $480,000 gives a loan amount of $384,000.", isCorrect: true },
      { text: "$96,000", rationale: "That is the down payment, not the loan amount.", isCorrect: false },
      { text: "$400,000", rationale: "That does not reflect a 20 percent down payment.", isCorrect: false },
      { text: "$576,000", rationale: "That is greater than the purchase price and therefore cannot be the loan amount here.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 98,
    question: "If a landlord collects $1,800 per month in rent, what is the annual gross scheduled rent?",
    answerOptions: [
      { text: "$21,600", rationale: "Multiply monthly rent by 12. $1,800 x 12 = $21,600.", isCorrect: true },
      { text: "$18,000", rationale: "That understates the annual rent.", isCorrect: false },
      { text: "$19,800", rationale: "That does not result from multiplying by 12.", isCorrect: false },
      { text: "$24,600", rationale: "That overstates the annual total.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 99,
    question: "An easement appurtenant most commonly benefits:",
    answerOptions: [
      { text: "An adjacent parcel called the dominant tenement", rationale: "An easement appurtenant benefits another parcel of land, known as the dominant tenement, and burdens the servient parcel.", isCorrect: true },
      { text: "A lender's note balance", rationale: "An easement is a property right, not a loan-balance benefit.", isCorrect: false },
      { text: "Only the current property manager personally", rationale: "That describes a personal benefit more like an easement in gross, not appurtenant.", isCorrect: false },
      { text: "The county tax roll", rationale: "Tax records are not the benefited parcel.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 100,
    question: "A property owner builds a fence that slightly crosses onto a neighbor's land. This is most likely:",
    answerOptions: [
      { text: "An encroachment", rationale: "An encroachment is an unauthorized physical intrusion of an improvement onto another's land.", isCorrect: true },
      { text: "A license", rationale: "A license is revocable permission to use land, not a physical intrusion.", isCorrect: false },
      { text: "A trade fixture", rationale: "A trade fixture is personal property installed by a commercial tenant for business use.", isCorrect: false },
      { text: "An emblement", rationale: "Emblements are annual crops.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 101,
    question: "A landlord refuses to allow a tenant with a disability to install grab bars at the tenant's expense where reasonable. This most likely raises:",
    answerOptions: [
      { text: "A fair housing reasonable-modification issue", rationale: "Federal fair housing law commonly tests reasonable modifications for persons with disabilities, especially when the tenant pays for the change.", isCorrect: true },
      { text: "Only a deed-recording issue", rationale: "This concerns housing rights, not deed recording.", isCorrect: false },
      { text: "Only a title-curative issue", rationale: "Title defects are unrelated to accessibility modifications.", isCorrect: false },
      { text: "Only an appraisal issue", rationale: "Appraisal is not the legal framework for this question.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 102,
    question: "An apartment ad stating 'adults only' most directly suggests prohibited discrimination based on:",
    answerOptions: [
      { text: "Familial status", rationale: "Ads that exclude children or families commonly implicate familial-status protections under fair housing law.", isCorrect: true },
      { text: "Topography", rationale: "Land shape and terrain are not the protected-class issue here.", isCorrect: false },
      { text: "Market value", rationale: "Value is not the protected category in question.", isCorrect: false },
      { text: "Recording priority", rationale: "Priority relates to title claims, not discriminatory advertising.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 103,
    question: "A seller's known defect that is not readily observable by a buyer is best described as:",
    answerOptions: [
      { text: "A latent defect", rationale: "A latent defect is hidden or not readily observable, unlike a patent defect that is visible on ordinary inspection.", isCorrect: true },
      { text: "A trade fixture", rationale: "A trade fixture is tenant-installed business property, not a hidden defect.", isCorrect: false },
      { text: "A gross lease", rationale: "That is a lease type, not a defect classification.", isCorrect: false },
      { text: "A bilateral contract", rationale: "That is a contract form, not a property-condition term.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 104,
    question: "The federal lead-based paint disclosure rules generally apply to many residential properties built before:",
    answerOptions: [
      { text: "1978", rationale: "The commonly tested trigger year for federal lead-based paint disclosure rules is 1978.", isCorrect: true },
      { text: "1995", rationale: "That is not the standard lead-disclosure trigger year.", isCorrect: false },
      { text: "2008", rationale: "That is far too recent for the federal lead-paint rule cutoff.", isCorrect: false },
      { text: "1964", rationale: "That predates the usual disclosure trigger but is not the rule's cutoff.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 105,
    question: "A buyer and seller agree to cancel an existing contract and replace it with a new one. This is most likely a:",
    answerOptions: [
      { text: "Novation", rationale: "A novation substitutes a new agreement or party and extinguishes the prior obligation.", isCorrect: true },
      { text: "Assignment", rationale: "An assignment transfers rights but does not necessarily extinguish the original contract in the same way.", isCorrect: false },
      { text: "Counteroffer", rationale: "A counteroffer rejects and replaces an offer during negotiation, not an already binding contract.", isCorrect: false },
      { text: "Option", rationale: "An option gives one party the right to buy or lease under stated terms, not a contract replacement.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 106,
    question: "A signed option to buy real estate is best described as:",
    answerOptions: [
      { text: "A unilateral contract binding the optionor if properly supported", rationale: "An option is commonly tested as binding the seller or optionor to keep the offer open if validly created, while the buyer may choose whether to exercise.", isCorrect: true },
      { text: "A leasehold estate only", rationale: "An option may relate to a future purchase right, not just a leasehold.", isCorrect: false },
      { text: "An automatic closing statement", rationale: "A closing statement is a settlement document, not an option contract.", isCorrect: false },
      { text: "A deed with full covenants", rationale: "A deed transfers title; an option creates a contractual right.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 107,
    question: "An agent who properly handles client funds and documents is most directly fulfilling the duty of:",
    answerOptions: [
      { text: "Accounting", rationale: "Accounting is the fiduciary duty requiring proper handling of money, documents, and property entrusted to the agent.", isCorrect: true },
      { text: "Conformity", rationale: "Conformity is an appraisal principle.", isCorrect: false },
      { text: "Regression", rationale: "Regression is also an appraisal principle.", isCorrect: false },
      { text: "Accession", rationale: "Accession concerns additions to property, not fiduciary handling.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 108,
    question: "If a principal later approves an agent's previously unauthorized act, the principal has most likely:",
    answerOptions: [
      { text: "Ratified the act", rationale: "Ratification occurs when a principal adopts an act after the fact, creating authority retroactively in many agency settings.", isCorrect: true },
      { text: "Commingled funds", rationale: "Commingling concerns improper mixing of funds, not approval of agency conduct.", isCorrect: false },
      { text: "Subordinated the lien", rationale: "Subordination changes lien priority, not agency authority.", isCorrect: false },
      { text: "Appraised the property", rationale: "Appraisal is unrelated to after-the-fact approval of an agent's action.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 109,
    question: "A parcel's value is harmed because the nearby highway creates constant noise. This most directly illustrates:",
    answerOptions: [
      { text: "External obsolescence", rationale: "Noise from an external source is a classic example of external obsolescence affecting value.", isCorrect: true },
      { text: "Plottage", rationale: "Plottage is added value from joining parcels.", isCorrect: false },
      { text: "Accretion", rationale: "Accretion is gradual land buildup by natural forces.", isCorrect: false },
      { text: "Assemblage", rationale: "Assemblage is the process of combining parcels.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 110,
    question: "A property with effective gross income of $150,000 and operating expenses of $60,000 has an NOI of:",
    answerOptions: [
      { text: "$90,000", rationale: "NOI equals effective gross income minus operating expenses. $150,000 - $60,000 = $90,000.", isCorrect: true },
      { text: "$210,000", rationale: "That incorrectly adds income and expenses.", isCorrect: false },
      { text: "$60,000", rationale: "That is the expense figure, not the NOI.", isCorrect: false },
      { text: "$150,000", rationale: "That ignores operating expenses entirely.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 111,
    question: "A municipality's building code requirement for smoke detectors is an example of:",
    answerOptions: [
      { text: "Police power", rationale: "Building and safety codes are classic examples of police power exercised to protect health and safety.", isCorrect: true },
      { text: "Eminent domain", rationale: "Eminent domain is the power to take property for public use with compensation.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat applies when property passes to the state due to lack of heirs.", isCorrect: false },
      { text: "Accession", rationale: "Accession is a property-law concept, not a government power.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 112,
    question: "A subdivision plat map is most associated with which legal description method?",
    answerOptions: [
      { text: "Lot-and-block", rationale: "Recorded subdivision maps are tied to the lot-and-block system of legal description.", isCorrect: true },
      { text: "Metes and bounds", rationale: "Metes and bounds describes land by directions and distances.", isCorrect: false },
      { text: "Rectangular survey only", rationale: "Rectangular survey uses township, range, and section.", isCorrect: false },
      { text: "Street-address description", rationale: "A mailing address is not the formal legal description method here.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 113,
    question: "A listing broker tells all cooperating brokers that no offers will be presented until next week, despite the seller's instruction to present them promptly. This most directly raises an issue of:",
    answerOptions: [
      { text: "Agency duty and obedience", rationale: "Agents must follow lawful client instructions and handle offers in accordance with their fiduciary duties.", isCorrect: true },
      { text: "Accretion and avulsion", rationale: "Those are land-change doctrines, not fiduciary issues.", isCorrect: false },
      { text: "Eminent domain procedure", rationale: "This is a brokerage conduct problem, not a government-taking issue.", isCorrect: false },
      { text: "Mill-rate calculation", rationale: "Tax math is unrelated to presentation of offers.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 114,
    question: "A seller receives a buyer's offer but changes the closing date before signing and sending it back. This response is generally a:",
    answerOptions: [
      { text: "Counteroffer", rationale: "Changing a material term before acceptance creates a counteroffer rather than an acceptance.", isCorrect: true },
      { text: "Novation", rationale: "A novation replaces an existing contract or party, not an offer under negotiation.", isCorrect: false },
      { text: "Completed closing", rationale: "A changed offer term does not complete the transaction.", isCorrect: false },
      { text: "Deed restriction", rationale: "A deed restriction is a title-use limitation, not a negotiation response.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 115,
    question: "Which item is most commonly prorated between buyer and seller at closing?",
    answerOptions: [
      { text: "Property taxes", rationale: "Property taxes are a standard prorated item because they are tied to time-based ownership responsibility.", isCorrect: true },
      { text: "The legal description method", rationale: "Legal description systems are not prorated costs.", isCorrect: false },
      { text: "The broker's license exam score", rationale: "Exam scores have nothing to do with closing adjustments.", isCorrect: false },
      { text: "The zoning district map", rationale: "Zoning maps are not typical prorated closing items.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 116,
    question: "A property sells for $250,000. If the broker earns a 6% commission and the listing broker keeps half, the listing broker's share is:",
    answerOptions: [
      { text: "$7,500", rationale: "Total commission is $250,000 x 0.06 = $15,000. Half of that is $7,500.", isCorrect: true },
      { text: "$15,000", rationale: "That is the total commission before the split.", isCorrect: false },
      { text: "$12,500", rationale: "That does not follow the stated commission and split.", isCorrect: false },
      { text: "$6,000", rationale: "That understates the listing broker's half of the total commission.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 117,
    question: "If a property is assessed at $200,000 and taxed at 25 mills, the annual property tax is:",
    answerOptions: [
      { text: "$5,000", rationale: "A 25-mill tax rate means $25 per $1,000 of assessed value. $200,000 / 1,000 = 200, and 200 x $25 = $5,000.", isCorrect: true },
      { text: "$2,500", rationale: "That uses the wrong mill-rate calculation.", isCorrect: false },
      { text: "$20,000", rationale: "That overstates the tax substantially.", isCorrect: false },
      { text: "$500", rationale: "That understates the annual tax.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 118,
    question: "A landlord tells a family with children that a certain building is 'for singles only.' This most directly suggests prohibited discrimination based on:",
    answerOptions: [
      { text: "Familial status", rationale: "Steering or refusing housing based on the presence of children commonly implicates familial-status protection.", isCorrect: true },
      { text: "Topographic elevation", rationale: "Terrain is not the issue in this discrimination question.", isCorrect: false },
      { text: "Title insurance classification", rationale: "Title insurance does not define this type of housing discrimination.", isCorrect: false },
      { text: "Loan amortization", rationale: "Loan repayment concepts are unrelated to familial-status discrimination.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 119,
    question: "The purpose of the EPA's lead hazard information pamphlet in covered transactions is primarily to:",
    answerOptions: [
      { text: "Inform buyers and tenants about lead-based paint risks", rationale: "The pamphlet is intended to educate parties in covered transactions about lead-based paint hazards and related precautions.", isCorrect: true },
      { text: "Set a property's assessed value", rationale: "Assessment is unrelated to the pamphlet's purpose.", isCorrect: false },
      { text: "Replace the deed at closing", rationale: "The pamphlet is an informational disclosure, not a conveyance document.", isCorrect: false },
      { text: "Waive the need for agency disclosure", rationale: "Lead disclosure does not replace agency-law disclosure requirements.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 120,
    question: "An appraiser estimates land value, adds the depreciated value of improvements, and arrives at an indication of value. This is the:",
    answerOptions: [
      { text: "Cost approach", rationale: "The cost approach starts with land value and adds the depreciated cost of improvements.", isCorrect: true },
      { text: "Sales comparison approach", rationale: "Sales comparison relies on comparable sales rather than replacement-cost logic.", isCorrect: false },
      { text: "Income approach", rationale: "Income capitalization centers on income stream, not land-plus-depreciated-improvement value.", isCorrect: false },
      { text: "Gross rent approach", rationale: "This is not the standard appraisal approach described here.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 121,
    question: "A housing provider who offers different rental terms because an applicant is from a particular country of origin most likely violates:",
    answerOptions: [
      { text: "Fair housing protections against national-origin discrimination", rationale: "National origin is a protected class under federal fair housing law, so different terms on that basis create a discrimination issue.", isCorrect: true },
      { text: "Only local building code rules", rationale: "This is a discrimination issue, not a code-enforcement issue.", isCorrect: false },
      { text: "Only tax assessment procedures", rationale: "Assessment rules do not govern discriminatory rental terms.", isCorrect: false },
      { text: "Only deed recording requirements", rationale: "Recording statutes are unrelated to national-origin discrimination.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 122,
    question: "An ad stating 'no kids' most directly raises a fair housing issue involving:",
    answerOptions: [
      { text: "Familial status", rationale: "Advertising that excludes children or families commonly implicates familial-status protections.", isCorrect: true },
      { text: "Accession", rationale: "Accession is a property-law concept, not a protected class.", isCorrect: false },
      { text: "Mortgage priority", rationale: "Lien priority is unrelated to discriminatory ad language.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat concerns transfer of property to the state when there are no heirs.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 123,
    question: "A seller knows the roof leaks but says nothing, and the defect is not readily observable. This is best described as:",
    answerOptions: [
      { text: "A latent material defect", rationale: "A hidden condition that would matter to a reasonable buyer is the classic example of a latent material defect.", isCorrect: true },
      { text: "A gross lease term", rationale: "Lease expense allocation is unrelated to hidden roof damage.", isCorrect: false },
      { text: "A metes-and-bounds element", rationale: "Legal descriptions do not classify physical defects.", isCorrect: false },
      { text: "A defeasance clause", rationale: "Defeasance is a mortgage concept, not a disclosure term.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 124,
    question: "The main purpose of seller disclosure laws is generally to help buyers learn about:",
    answerOptions: [
      { text: "Known material property conditions", rationale: "Disclosure laws are aimed at informing buyers about material facts or conditions that may affect value or desirability.", isCorrect: true },
      { text: "A broker's commission split", rationale: "Commission arrangements are not the core purpose of property-condition disclosure laws.", isCorrect: false },
      { text: "The county clerk's work hours", rationale: "Office schedules are irrelevant to material property disclosures.", isCorrect: false },
      { text: "The appraiser's license number", rationale: "Professional licensing details are not the central buyer-disclosure issue.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 125,
    question: "A buyer and seller sign a contract, but the buyer has not yet performed. The contract is best described as:",
    answerOptions: [
      { text: "Executory", rationale: "A contract is executory when it has been formed but one or more parties still have duties left to perform.", isCorrect: true },
      { text: "Executed", rationale: "Executed generally means fully performed, not merely signed.", isCorrect: false },
      { text: "Void", rationale: "A valid signed contract is not void simply because performance remains.", isCorrect: false },
      { text: "Rescinded", rationale: "Rescission means the contract has been canceled.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 126,
    question: "A valid contract usually requires competent parties, lawful objective, consideration, and:",
    answerOptions: [
      { text: "Mutual agreement", rationale: "Mutual agreement is one of the fundamental elements of a valid contract.", isCorrect: true },
      { text: "A title policy", rationale: "Title insurance is not required to create a valid contract.", isCorrect: false },
      { text: "A survey monument", rationale: "Boundary markers are unrelated to contract formation.", isCorrect: false },
      { text: "A lien waiver", rationale: "Lien waivers are not a general element of contract validity.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 127,
    question: "A broker who tells a seller every material fact learned about a buyer's offer is most directly demonstrating:",
    answerOptions: [
      { text: "Disclosure and loyalty", rationale: "Agents owe clients duties that include loyalty and disclosure of material information relevant to the transaction.", isCorrect: true },
      { text: "Avulsion and accretion", rationale: "Those are land-change doctrines, not fiduciary duties.", isCorrect: false },
      { text: "Plottage and assemblage", rationale: "Those are valuation or parcel-combination concepts, not agency duties.", isCorrect: false },
      { text: "Subordination and defeasance", rationale: "Those are financing concepts, not fiduciary duties.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 128,
    question: "When an agent reveals a seller's minimum acceptable price without permission, the clearest fiduciary breach is:",
    answerOptions: [
      { text: "Confidentiality", rationale: "An agent must protect confidential negotiating information unless authorized to reveal it.", isCorrect: true },
      { text: "Conformity", rationale: "Conformity is an appraisal principle, not a fiduciary duty.", isCorrect: false },
      { text: "Accession", rationale: "Accession is a property-law concept, not an agency duty.", isCorrect: false },
      { text: "Regression", rationale: "Regression is an appraisal principle.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 129,
    question: "A property that is worth less because the lot is oddly shaped may reflect a form of:",
    answerOptions: [
      { text: "Functional obsolescence", rationale: "A design or layout problem within the property itself, such as poor utility from an odd shape, is functional obsolescence.", isCorrect: true },
      { text: "External obsolescence", rationale: "External obsolescence comes from influences outside the property boundaries.", isCorrect: false },
      { text: "Progression", rationale: "Progression is a value principle tied to neighboring properties, not internal layout deficiency.", isCorrect: false },
      { text: "Plottage", rationale: "Plottage is added value from combining parcels.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 130,
    question: "An appraiser's estimate of value as of a specific date is called an opinion of:",
    answerOptions: [
      { text: "Market value", rationale: "Appraisal reports typically state an opinion of market value as of an effective date.", isCorrect: true },
      { text: "Marketability of title", rationale: "Title marketability is a legal title concept, not the appraisal conclusion described here.", isCorrect: false },
      { text: "Assessed ownership", rationale: "That is not the standard valuation term.", isCorrect: false },
      { text: "Lease priority", rationale: "Lease ranking is unrelated to the appraiser's value opinion.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 131,
    question: "A county adopts a new setback rule requiring buildings to be farther from the street. This is an example of:",
    answerOptions: [
      { text: "Land-use regulation under police power", rationale: "Setback rules are classic zoning-type land-use regulations enacted under the government's police power.", isCorrect: true },
      { text: "Eminent domain compensation", rationale: "A setback rule regulates use; it does not itself describe a taking with compensation.", isCorrect: false },
      { text: "A title warranty", rationale: "Title warranties arise in deeds, not zoning ordinances.", isCorrect: false },
      { text: "A mortgage acceleration", rationale: "Acceleration is a loan-default concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 132,
    question: "A recorded plat that identifies numbered lots and blocks is most closely tied to:",
    answerOptions: [
      { text: "The lot-and-block system", rationale: "Numbered lots and blocks on a recorded plat are the hallmark of the lot-and-block legal description method.", isCorrect: true },
      { text: "The metes-and-bounds system", rationale: "Metes and bounds uses courses and distances rather than lot and block references.", isCorrect: false },
      { text: "The township-range system only", rationale: "Township-range uses sections, townships, and ranges.", isCorrect: false },
      { text: "A street-address system", rationale: "A mailing address is not the formal legal description here.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 133,
    question: "The settlement statement at closing is primarily used to:",
    answerOptions: [
      { text: "Summarize the financial debits and credits of the transaction", rationale: "The settlement statement is the document used to itemize charges, credits, and the funds needed to close.", isCorrect: true },
      { text: "Transfer title by warranty deed", rationale: "The deed transfers title; the settlement statement accounts for the money.", isCorrect: false },
      { text: "Create the listing agreement", rationale: "Listing agreements are created earlier in the transaction.", isCorrect: false },
      { text: "Issue the property survey", rationale: "A survey is a separate service and document.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 134,
    question: "A buyer takes title subject to existing recorded easements. This most directly affects:",
    answerOptions: [
      { text: "The quality and use of title", rationale: "Recorded easements are encumbrances that can affect the use and quality of title a buyer receives.", isCorrect: true },
      { text: "Whether the buyer passed the licensing exam", rationale: "Licensing results are unrelated to easements on title.", isCorrect: false },
      { text: "The broker's agency relationship only", rationale: "Agency relationships do not define the title burden created by easements.", isCorrect: false },
      { text: "The amount of the surveyor's mileage charge", rationale: "Surveyor fees are not the key legal effect of recorded easements.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 135,
    question: "If annual property taxes are $3,650, the daily proration using a 365-day year is:",
    answerOptions: [
      { text: "$10", rationale: "Daily tax proration is $3,650 / 365 = $10 per day.", isCorrect: true },
      { text: "$12", rationale: "That overstates the daily amount.", isCorrect: false },
      { text: "$36.50", rationale: "That reflects the wrong divisor.", isCorrect: false },
      { text: "$1", rationale: "That understates the daily rate.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 136,
    question: "A closing statement shows a $1,200 annual insurance premium paid in advance. Using a 12-month year, the monthly proration is:",
    answerOptions: [
      { text: "$100", rationale: "Monthly proration is $1,200 / 12 = $100 per month.", isCorrect: true },
      { text: "$120", rationale: "That is not the monthly result of dividing by 12.", isCorrect: false },
      { text: "$10", rationale: "That is far too low.", isCorrect: false },
      { text: "$1,200", rationale: "That is the annual premium, not the monthly amount.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 137,
    question: "A seller tells an agent to keep the property on the market while an offer is being negotiated. The agent's main duty is to:",
    answerOptions: [
      { text: "Follow the seller's lawful instructions", rationale: "Agents owe obedience to lawful client instructions, subject to the rest of their fiduciary duties and applicable law.", isCorrect: true },
      { text: "Accept the offer without approval", rationale: "The agent ordinarily cannot bind the seller without authority.", isCorrect: false },
      { text: "Reveal the seller's minimum price to every buyer", rationale: "That would violate confidentiality.", isCorrect: false },
      { text: "Refuse to present later offers", rationale: "That may conflict with the client's instructions and the agent's duties.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 138,
    question: "A signed purchase contract that has been fully carried out by all parties is generally described as:",
    answerOptions: [
      { text: "Executed", rationale: "A contract becomes executed when the parties have completed the required performance.", isCorrect: true },
      { text: "Executory", rationale: "Executory means performance remains outstanding.", isCorrect: false },
      { text: "Voidable", rationale: "Voidable means one party may avoid the contract for a legal reason.", isCorrect: false },
      { text: "Illusory", rationale: "An illusory promise lacks true commitment, not completed performance.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 139,
    question: "A buyer asks for the appraiser's opinion of the value of the site as if vacant. That is most directly a question about:",
    answerOptions: [
      { text: "Land value", rationale: "Site or land value is the appraiser's estimate of the value of the land itself as though vacant and available for its highest and best use.", isCorrect: true },
      { text: "Debt service", rationale: "Debt service concerns loan payments, not land valuation.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat is a transfer-by-law concept.", isCorrect: false },
      { text: "Dual agency", rationale: "Agency relationships do not define land value.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 140,
    question: "A seller who intentionally hides termite damage from a buyer is creating the strongest issue in the area of:",
    answerOptions: [
      { text: "Property disclosure and concealment", rationale: "Intentional hiding of a significant defect is the classic disclosure and concealment problem.", isCorrect: true },
      { text: "Legal description methods", rationale: "Legal descriptions do not address hidden termite damage.", isCorrect: false },
      { text: "Mortgage underwriting ratios", rationale: "Loan ratios are unrelated to concealment of physical defects.", isCorrect: false },
      { text: "Deed acknowledgment only", rationale: "Acknowledgment is a deed formalization issue, not the main defect-concealment issue.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 141,
    question: "A lender's estimate of the borrower's ability to repay a mortgage most directly focuses on the borrower's:",
    answerOptions: [
      { text: "Income, debts, and credit profile", rationale: "Underwriting commonly examines income, debt obligations, credit history, and related repayment capacity factors.", isCorrect: true },
      { text: "Favorite paint color", rationale: "Personal design preferences do not determine repayment ability.", isCorrect: false },
      { text: "Preferred title company logo", rationale: "Brand preference is unrelated to underwriting.", isCorrect: false },
      { text: "Lot-and-block legal description", rationale: "Legal descriptions identify land, not borrower creditworthiness.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 142,
    question: "A mortgage in which the borrower promises to repay in regular installments of principal and interest is commonly described as:",
    answerOptions: [
      { text: "An amortized loan", rationale: "Amortization means periodic payments reduce both principal and interest over time according to a schedule.", isCorrect: true },
      { text: "A gross lease", rationale: "That is a lease expense-allocation term, not a loan structure.", isCorrect: false },
      { text: "A quitclaim transfer", rationale: "That is a deed concept, not a repayment structure.", isCorrect: false },
      { text: "An easement appurtenant", rationale: "That is a property right benefiting another parcel.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 143,
    question: "A borrower who pays off a mortgage before maturity may face which financing concept if the note allows it?",
    answerOptions: [
      { text: "A prepayment penalty", rationale: "Some loan documents impose a charge if the borrower prepays the debt early.", isCorrect: true },
      { text: "A deed restriction", rationale: "Deed restrictions govern use of land, not early loan payoff.", isCorrect: false },
      { text: "An estoppel certificate", rationale: "That term is unrelated to prepayment penalties in this context.", isCorrect: false },
      { text: "A variance fee", rationale: "Variance costs concern zoning relief, not mortgage payoff.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 144,
    question: "Under federal fair housing law, refusing to rent because an applicant is of a certain religion is most likely:",
    answerOptions: [
      { text: "Illegal discrimination", rationale: "Religion is a protected class under federal fair housing law.", isCorrect: true },
      { text: "Allowed if rent is higher", rationale: "Different pricing does not legalize discrimination.", isCorrect: false },
      { text: "Only a financing issue", rationale: "This is a fair housing issue, not a lending issue only.", isCorrect: false },
      { text: "Only a closing issue", rationale: "Closing mechanics are unrelated to discriminatory rental refusal.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 145,
    question: "A landlord tells an agent not to show a property to families with children. The agent's best response is to recognize this as:",
    answerOptions: [
      { text: "A fair housing problem involving familial status", rationale: "Instructions to exclude families with children commonly violate familial-status protections.", isCorrect: true },
      { text: "A lawful marketing preference", rationale: "Protected-class discrimination is not a lawful preference.", isCorrect: false },
      { text: "A title insurance underwriting rule", rationale: "Title insurers do not set discriminatory showing rules.", isCorrect: false },
      { text: "A standard appraisal adjustment", rationale: "Appraisal practice is unrelated to discriminatory screening.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 146,
    question: "A zoning ordinance that controls minimum lot size is most directly an exercise of:",
    answerOptions: [
      { text: "Police power", rationale: "Zoning controls such as lot-size requirements are enacted under the government's police power.", isCorrect: true },
      { text: "Escheat", rationale: "Escheat transfers property to the state when there are no heirs.", isCorrect: false },
      { text: "Defeasance", rationale: "Defeasance is a mortgage-lien release concept.", isCorrect: false },
      { text: "Accession", rationale: "Accession concerns additions to property.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 147,
    question: "The method of legal description that uses directions and distances from a point of beginning is:",
    answerOptions: [
      { text: "Metes and bounds", rationale: "Metes and bounds describes land by measurements, directions, and monuments beginning from a point of beginning.", isCorrect: true },
      { text: "Lot and block", rationale: "Lot and block uses a recorded plat reference rather than courses and distances.", isCorrect: false },
      { text: "Rectangular survey", rationale: "Rectangular survey uses township, range, and section.", isCorrect: false },
      { text: "Street address", rationale: "A mailing address is not a complete legal description.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 148,
    question: "An appraiser using recent sales of similar nearby properties is primarily relying on the:",
    answerOptions: [
      { text: "Sales comparison approach", rationale: "The sales comparison approach uses comparable recent sales to estimate value.", isCorrect: true },
      { text: "Cost approach", rationale: "The cost approach focuses on land value plus depreciated improvements.", isCorrect: false },
      { text: "Income approach", rationale: "The income approach capitalizes benefits from income-producing property.", isCorrect: false },
      { text: "Amortization approach", rationale: "Amortization is a loan concept, not a standard appraisal approach.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 149,
    question: "A commercial property with NOI of $84,000 and a capitalization rate of 7% would indicate a value of:",
    answerOptions: [
      { text: "$1,200,000", rationale: "Value equals NOI divided by the cap rate. $84,000 / 0.07 = $1,200,000.", isCorrect: true },
      { text: "$588,000", rationale: "That does not result from dividing by the stated cap rate.", isCorrect: false },
      { text: "$84,000", rationale: "That is the NOI, not the value indication.", isCorrect: false },
      { text: "$1,470,000", rationale: "That overstates the result.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 150,
    question: "A seller provides a disclosure form stating that a basement water issue occurred last year and was repaired. This most directly serves the purpose of:",
    answerOptions: [
      { text: "Informing the buyer about a known material condition", rationale: "Disclosure forms are meant to inform buyers about known material conditions that could affect the property.", isCorrect: true },
      { text: "Creating a new deed covenant", rationale: "Disclosure forms do not create title covenants.", isCorrect: false },
      { text: "Waiving lender underwriting", rationale: "Disclosures do not remove lender qualification requirements.", isCorrect: false },
      { text: "Changing the property's assessed value", rationale: "Assessment is a separate tax matter.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 151,
    question: "A New York real estate salesperson may lawfully perform brokerage activities only when:",
    answerOptions: [
      { text: "Sponsored by a licensed New York broker", rationale: "A New York real estate salesperson cannot operate independently. The salesperson must be associated with and sponsored by a licensed New York broker.", isCorrect: true },
      { text: "Working independently after passing the exam", rationale: "Passing the exam alone is not enough. A salesperson still must work under a licensed broker.", isCorrect: false },
      { text: "Registered with a local REALTOR association", rationale: "Association membership is optional and does not create legal authority to practice.", isCorrect: false },
      { text: "Employed by any attorney handling closings", rationale: "Closing involvement by an attorney does not replace the statutory requirement for broker sponsorship.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 152,
    question: "Under New York agency law, a broker may act as a dual agent only if:",
    answerOptions: [
      { text: "Both parties give informed written consent", rationale: "Dual agency is permitted in New York only when both parties knowingly consent in writing after being informed of the limitations dual agency creates.", isCorrect: true },
      { text: "The broker reduces the commission", rationale: "Commission size has nothing to do with whether dual agency is legally authorized.", isCorrect: false },
      { text: "The transaction is residential rather than commercial", rationale: "Dual agency rules turn on disclosure and consent, not on residential versus commercial property type.", isCorrect: false },
      { text: "The buyer and seller are represented by attorneys", rationale: "Attorney involvement does not eliminate the broker's duty to obtain informed written consent.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 153,
    question: "Which fiduciary duty is most directly violated if an agent reveals a buyer's maximum price without permission?",
    answerOptions: [
      { text: "Confidentiality", rationale: "An agent must protect a client's confidential information, including negotiating positions such as the highest price a buyer would pay.", isCorrect: true },
      { text: "Accounting", rationale: "Accounting concerns proper handling of money and property, not disclosure of negotiation information.", isCorrect: false },
      { text: "Reasonable care", rationale: "Reasonable care is a fiduciary duty, but the direct violation here is unauthorized disclosure of confidential information.", isCorrect: false },
      { text: "Obedience", rationale: "Obedience means following lawful client instructions. The clearest tested duty breached here is confidentiality.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 154,
    question: "The New York Housing and Anti-Discrimination Disclosure Form must generally be provided:",
    answerOptions: [
      { text: "At first substantive contact", rationale: "New York requires the Housing and Anti-Discrimination Disclosure Form to be presented at first substantive contact with a prospective purchaser, tenant, seller, or landlord.", isCorrect: true },
      { text: "Only when an offer is submitted", rationale: "That is too late. The timing requirement is much earlier.", isCorrect: false },
      { text: "After the closing attorney approves the contract", rationale: "This form is not tied to attorney approval of the contract.", isCorrect: false },
      { text: "Only at an open house", rationale: "Open houses may trigger fair housing notice duties, but the disclosure form is not limited to that setting.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 155,
    question: "A New York buyer of a cooperative apartment typically receives:",
    answerOptions: [
      { text: "Shares in the corporation and a proprietary lease", rationale: "A cooperative buyer usually purchases shares in the cooperative corporation and receives a proprietary lease giving occupancy rights.", isCorrect: true },
      { text: "A deed to the airspace inside the unit only", rationale: "That answer describes condominium-style deed ownership more than cooperative ownership.", isCorrect: false },
      { text: "A fee simple deed plus a separate lease from the board", rationale: "A coop buyer normally does not receive a fee simple deed to the unit.", isCorrect: false },
      { text: "A life estate with a right of survivorship", rationale: "That is a form of ownership estate, not the typical structure of a cooperative purchase.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 156,
    question: "Which local body most commonly hears requests for zoning variances?",
    answerOptions: [
      { text: "Zoning board of appeals", rationale: "Variance requests are commonly heard by the zoning board of appeals, which handles relief from strict zoning requirements.", isCorrect: true },
      { text: "Tax assessor's office", rationale: "The assessor deals with valuation and taxation issues, not zoning variances.", isCorrect: false },
      { text: "Planning board", rationale: "Planning boards often review subdivisions and site plans, but variances are usually handled by the zoning board of appeals.", isCorrect: false },
      { text: "County clerk", rationale: "The county clerk is not the zoning-variance decision maker.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 157,
    question: "Which appraisal approach is usually most relevant when valuing an owner-occupied one-family home?",
    answerOptions: [
      { text: "Sales comparison approach", rationale: "Owner-occupied residential property is typically valued most directly by comparing it to similar recently sold homes.", isCorrect: true },
      { text: "Income approach", rationale: "The income approach is generally more important for income-producing property.", isCorrect: false },
      { text: "Cost recovery approach", rationale: "That is not one of the standard three appraisal approaches tested on the exam.", isCorrect: false },
      { text: "Net operating approach", rationale: "This is not a recognized standard appraisal approach; NOI is part of the income approach.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 158,
    question: "Net operating income for an income-producing property is calculated by subtracting from gross income:",
    answerOptions: [
      { text: "Operating expenses", rationale: "NOI is gross income minus operating expenses, but before debt service and income taxes.", isCorrect: true },
      { text: "Debt service", rationale: "Debt service is not subtracted when calculating NOI.", isCorrect: false },
      { text: "Depreciation only", rationale: "Depreciation alone does not define NOI; operating expenses do.", isCorrect: false },
      { text: "Income taxes only", rationale: "Income taxes are not part of the standard NOI calculation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 159,
    question: "A New York unlicensed assistant may lawfully:",
    answerOptions: [
      { text: "Perform clerical support tasks under broker supervision", rationale: "Unlicensed assistants may perform clerical and administrative tasks, but they may not perform licensed brokerage activities.", isCorrect: true },
      { text: "Negotiate commission splits with cooperating brokers", rationale: "Negotiating brokerage compensation is a licensed activity.", isCorrect: false },
      { text: "Show listed property alone to prospective buyers", rationale: "Showing property as part of brokerage services is generally a licensed activity.", isCorrect: false },
      { text: "Receive payment based on completed transactions while unlicensed", rationale: "Transaction-based compensation generally requires licensure and payment through the broker.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 160,
    question: "A New York blind ad is problematic because it:",
    answerOptions: [
      { text: "Fails to identify the broker or brokerage behind the ad", rationale: "Advertising rules require the public to be able to identify the responsible broker or brokerage. Hiding that identity creates a blind ad problem.", isCorrect: true },
      { text: "Includes too many photographs of the property", rationale: "Photo quantity is not the core blind-ad issue.", isCorrect: false },
      { text: "Uses social media rather than print media", rationale: "The medium is not the issue; the missing broker identity is.", isCorrect: false },
      { text: "Mentions a property listed with another broker", rationale: "That may raise separate authorization issues, but blind advertising specifically concerns lack of brokerage identification.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 161,
    question: "If a property has an annual NOI of $54,000 and an 8% capitalization rate, the indicated value is:",
    answerOptions: [
      { text: "$675,000", rationale: "Value equals NOI divided by cap rate. $54,000 / 0.08 = $675,000.", isCorrect: true },
      { text: "$432,000", rationale: "That result does not come from dividing NOI by the capitalization rate.", isCorrect: false },
      { text: "$62,000", rationale: "That is far too low and reflects the wrong operation.", isCorrect: false },
      { text: "$6,750,000", rationale: "That overstates value by moving the decimal point incorrectly.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 162,
    question: "The New York STAR program generally relates to:",
    answerOptions: [
      { text: "School tax relief for qualifying homeowners", rationale: "STAR is a New York tax-relief concept tied to school taxes for qualifying homeowners.", isCorrect: true },
      { text: "Mortgage insurance for first-time buyers", rationale: "STAR is not a mortgage-insurance program.", isCorrect: false },
      { text: "State licensing reciprocity", rationale: "STAR has nothing to do with license reciprocity.", isCorrect: false },
      { text: "Rent stabilization for cooperative shareholders", rationale: "STAR is a tax-relief issue, not a rent-regulation concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 163,
    question: "Which best describes a mortgage recording tax in New York?",
    answerOptions: [
      { text: "A tax imposed when a mortgage is recorded", rationale: "A mortgage recording tax is tied to recording the mortgage lien, not simply to owning or selling real estate.", isCorrect: true },
      { text: "A tax on annual rental income from a mortgaged property", rationale: "Income taxes and mortgage recording taxes are different concepts.", isCorrect: false },
      { text: "A fee paid to renew a salesperson license", rationale: "Licensing fees are unrelated to mortgage recording tax.", isCorrect: false },
      { text: "A federal transfer tax on every real estate sale", rationale: "Mortgage recording tax is not a universal federal sale tax.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 164,
    question: "The main purpose of a title search before closing is to:",
    answerOptions: [
      { text: "Identify defects, liens, encumbrances, and ownership issues in the record", rationale: "A title search is performed to uncover recorded matters that could affect ownership, transfer, or financing.", isCorrect: true },
      { text: "Establish a property's fair market rent", rationale: "Rental analysis is unrelated to the purpose of a title search.", isCorrect: false },
      { text: "Determine whether the buyer qualifies for a mortgage", rationale: "Loan qualification is a lender underwriting issue, not a title-search function.", isCorrect: false },
      { text: "Set the annual property tax assessment", rationale: "Assessment is handled through taxing authorities, not the title search.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 165,
    question: "A leasehold estate with a definite beginning and ending date is called:",
    answerOptions: [
      { text: "An estate for years", rationale: "An estate for years is the leasehold estate with a fixed start and fixed end date, regardless of whether the term is days, months, or years.", isCorrect: true },
      { text: "A tenancy at will", rationale: "A tenancy at will does not have a definite fixed ending date.", isCorrect: false },
      { text: "A life estate", rationale: "A life estate is a freehold ownership interest, not a leasehold.", isCorrect: false },
      { text: "A tenancy by the entirety", rationale: "That is a marital form of ownership, not a leasehold estate.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 166,
    question: "Which best describes actual cash value in property insurance?",
    answerOptions: [
      { text: "Replacement cost minus depreciation", rationale: "Actual cash value generally reflects replacement cost reduced by depreciation, unlike replacement-cost coverage that does not deduct depreciation in the same way.", isCorrect: true },
      { text: "The amount of the annual deductible", rationale: "A deductible is a separate policy feature, not the definition of actual cash value.", isCorrect: false },
      { text: "The original purchase price of the property", rationale: "Original price does not define actual cash value for insurance purposes.", isCorrect: false },
      { text: "The mortgage balance at the time of loss", rationale: "Loan balance is not the insurance valuation method.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 167,
    question: "The primary distinction between a condominium and a cooperative in New York is that a condominium buyer usually receives:",
    answerOptions: [
      { text: "A deed to the unit plus an undivided interest in common elements", rationale: "A condominium owner typically receives deeded ownership of the unit and a shared interest in the common elements, unlike a cooperative shareholder.", isCorrect: true },
      { text: "Only a proprietary lease and no ownership interest", rationale: "That better describes a cooperative structure, not a condominium.", isCorrect: false },
      { text: "A monthly tax bill in place of closing documents", rationale: "Tax bills do not define the ownership form.", isCorrect: false },
      { text: "Shares in a lender rather than shares in a corporation", rationale: "That does not describe condominium ownership.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 168,
    question: "If a salesperson website omits the required broker identification on search-result pages, the main compliance issue is:",
    answerOptions: [
      { text: "Web advertising disclosure failure", rationale: "New York advertising rules extend beyond the homepage, so required brokerage disclosures must appear on web pages including property-search results.", isCorrect: true },
      { text: "Commingling of funds", rationale: "Commingling concerns mishandling client money, not advertising disclosure.", isCorrect: false },
      { text: "Failure to maintain escrow records", rationale: "Escrow-record rules concern trust funds, not website pages.", isCorrect: false },
      { text: "Unauthorized practice of law", rationale: "The issue described is advertising compliance, not legal advice.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 169,
    question: "Which statement about a New York cease and desist zone is correct?",
    answerOptions: [
      { text: "Certain solicitation of homeowners on the cease and desist list is prohibited", rationale: "Cease and desist zones are designed to restrict prohibited real estate solicitation directed to homeowners who placed themselves on the applicable list.", isCorrect: true },
      { text: "All residential sales are banned within the zone", rationale: "The zone limits solicitation, not lawful buying and selling itself.", isCorrect: false },
      { text: "Only out-of-state brokers may not advertise there", rationale: "The rule is not limited to out-of-state brokers.", isCorrect: false },
      { text: "The zone replaces all fair housing rules within its boundaries", rationale: "Fair housing rules still apply; cease and desist rules are an additional regulation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 170,
    question: "A New York salesperson's license term is:",
    answerOptions: [
      { text: "Two years", rationale: "The New York Department of State states that a real estate salesperson license term is two years.", isCorrect: true },
      { text: "One year", rationale: "That is shorter than the actual New York salesperson license term.", isCorrect: false },
      { text: "Three years", rationale: "New York does not use a three-year salesperson license term.", isCorrect: false },
      { text: "Five years", rationale: "Five years is not the New York salesperson license term.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 171,
    question: "How many hours of approved qualifying education are currently required for a New York real estate salesperson license?",
    answerOptions: [
      { text: "77 hours", rationale: "New York currently requires 77 hours of approved qualifying education for the salesperson license path.", isCorrect: true },
      { text: "45 hours", rationale: "That is below the current New York salesperson education requirement.", isCorrect: false },
      { text: "60 hours", rationale: "New York's current approved-hour requirement is higher than 60.", isCorrect: false },
      { text: "90 hours", rationale: "That is not the current New York salesperson qualifying-hour requirement.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 172,
    question: "The New York buyer and seller agency disclosure form is primarily designed to:",
    answerOptions: [
      { text: "Explain the agency relationship and the duties owed", rationale: "The agency disclosure form is intended to explain representation choices and the duties associated with those relationships.", isCorrect: true },
      { text: "Replace the purchase contract", rationale: "The agency disclosure form does not replace the contract of sale.", isCorrect: false },
      { text: "Set the final commission split between offices", rationale: "Commission splits are separate brokerage matters.", isCorrect: false },
      { text: "Transfer title at closing", rationale: "Title is transferred by deed, not by agency disclosure.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 173,
    question: "In New York, a broker's agent usually works with or through:",
    answerOptions: [
      { text: "Another agent who has the direct relationship with the client", rationale: "A broker's agent assists another agent, such as a listing agent or buyer's agent, rather than having the direct client relationship personally.", isCorrect: true },
      { text: "The county clerk only", rationale: "The county clerk is unrelated to this agency role.", isCorrect: false },
      { text: "The tax assessor only", rationale: "The tax assessor has no role in defining broker's-agent relationships.", isCorrect: false },
      { text: "No one, because a broker's agent is always the principal", rationale: "A broker's agent is not the principal and does not stand outside the agency structure.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 174,
    question: "If a New York property owner challenges an assessment as excessive, the matter is commonly first heard by:",
    answerOptions: [
      { text: "The board of assessment review or grievance board", rationale: "Assessment grievances typically begin with the local board of assessment review or grievance process.", isCorrect: true },
      { text: "The zoning board of appeals", rationale: "Zoning boards deal with zoning relief, not tax-assessment grievances.", isCorrect: false },
      { text: "The planning board", rationale: "Planning boards review development and planning issues, not assessment protests.", isCorrect: false },
      { text: "The Department of State licensing unit", rationale: "Licensing authorities do not handle local property-assessment challenges.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 175,
    question: "Which statement best describes a New York cease and desist zone mailing problem?",
    answerOptions: [
      { text: "Sending solicitation to a homeowner on the cease and desist list can violate the rule", rationale: "Cease and desist zone rules focus on prohibited solicitation directed to listed homeowners, including forms of direct marketing.", isCorrect: true },
      { text: "Sending mail is always allowed if it contains no brokerage logo", rationale: "Removing a logo does not eliminate the solicitation issue.", isCorrect: false },
      { text: "The rule applies only to commercial property owners", rationale: "Cease and desist zones are associated with homeowner solicitation restrictions.", isCorrect: false },
      { text: "Only telephone calls are restricted, not mail", rationale: "The issue is broader than phone calls alone.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 176,
    question: "A New York business card must include the broker or brokerage name because the state wants to ensure:",
    answerOptions: [
      { text: "The public can identify the licensed business responsible for the communication", rationale: "Advertising rules are aimed at transparency, including identifying the responsible brokerage.", isCorrect: true },
      { text: "Only attorneys may advertise real estate services", rationale: "Real estate licensees may advertise, but they must do so in compliance with brokerage-identification rules.", isCorrect: false },
      { text: "Every card also works as a deed", rationale: "A business card is not a conveyance instrument.", isCorrect: false },
      { text: "The salesperson can practice without broker supervision", rationale: "Business-card content does not eliminate the broker-supervision requirement.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 177,
    question: "Which of the following best describes a cooperative board's role in many New York coop purchases?",
    answerOptions: [
      { text: "The board may approve or reject the prospective purchaser", rationale: "Board approval is a defining feature of many New York coop transactions.", isCorrect: true },
      { text: "The board sets state licensing law", rationale: "Licensing law is set by state law and regulation, not by a coop board.", isCorrect: false },
      { text: "The board issues the deed directly from the county clerk's office", rationale: "A coop usually does not involve a deed to the unit in the same way a condo does.", isCorrect: false },
      { text: "The board replaces the lender's underwriting decision", rationale: "The board may have approval power, but it does not become the lender.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 178,
    question: "If a borrower is prequalified but not preapproved, the main concern for a seller is that:",
    answerOptions: [
      { text: "The borrower's financing position has not been reviewed as thoroughly by the lender", rationale: "Prequalification is generally more preliminary and less reliable than preapproval.", isCorrect: true },
      { text: "The borrower already has a binding mortgage commitment", rationale: "Prequalification is not the same as a mortgage commitment.", isCorrect: false },
      { text: "The borrower has already closed", rationale: "Prequalification occurs long before closing.", isCorrect: false },
      { text: "The borrower is exempt from appraisal requirements", rationale: "Prequalification does not waive ordinary underwriting conditions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 179,
    question: "In New York, a mortgage commitment is best described as:",
    answerOptions: [
      { text: "A lender's written promise to make the loan subject to stated terms and conditions", rationale: "A mortgage commitment is more substantial than prequalification because it reflects lender approval subject to conditions.", isCorrect: true },
      { text: "A final deed transfer to the buyer", rationale: "That describes conveyance at closing, not a financing commitment.", isCorrect: false },
      { text: "A guarantee that no appraisal will be needed", rationale: "Commitments may still contain appraisal or other conditions.", isCorrect: false },
      { text: "A Department of State approval for license renewal", rationale: "The commitment is a lending concept, not a licensing concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 180,
    question: "A New York broker who keeps required transaction records electronically generally must still:",
    answerOptions: [
      { text: "Maintain them in a retrievable form for the required retention period", rationale: "Electronic storage is generally acceptable if records are properly maintained and retrievable for the required period.", isCorrect: true },
      { text: "Destroy them within 30 days to protect privacy", rationale: "That would conflict with record-retention requirements.", isCorrect: false },
      { text: "Send every file to the local planning board", rationale: "Planning boards do not receive brokerage transaction files as a general rule.", isCorrect: false },
      { text: "Convert all records into deeds before closing", rationale: "Most brokerage records are not deeds and do not become deeds.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 181,
    question: "The purpose of the Property Condition Disclosure Statement is to:",
    answerOptions: [
      { text: "Provide condition information from the seller in covered transactions", rationale: "The statement is a seller-condition disclosure document; it does not replace appraisal, financing, or buyer due diligence.", isCorrect: true },
      { text: "Replace the appraisal report", rationale: "An appraisal addresses value, not seller condition disclosure.", isCorrect: false },
      { text: "Set the final mortgage interest rate", rationale: "Interest rates are lending matters, not property-condition disclosure issues.", isCorrect: false },
      { text: "Eliminate all need for buyer inspections", rationale: "A disclosure statement does not remove the value of inspections or buyer due diligence.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 182,
    question: "Which best describes actual cash flow for an income property owner?",
    answerOptions: [
      { text: "Income remaining after operating expenses and debt service", rationale: "Cash flow differs from NOI because cash flow reflects financing costs as well.", isCorrect: true },
      { text: "Gross rent before any deductions", rationale: "That is not cash flow because no expenses or debt have been considered.", isCorrect: false },
      { text: "Assessed value multiplied by tax rate", rationale: "That relates to property taxes, not cash flow.", isCorrect: false },
      { text: "Replacement cost less depreciation", rationale: "That is tied to the cost approach to value, not cash flow.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 183,
    question: "If a property is assessed at 60% of market value and the market value is $500,000, the assessed value is:",
    answerOptions: [
      { text: "$300,000", rationale: "Assessed value equals market value multiplied by the assessment ratio. $500,000 x 0.60 = $300,000.", isCorrect: true },
      { text: "$200,000", rationale: "That does not reflect 60 percent of $500,000.", isCorrect: false },
      { text: "$560,000", rationale: "Assessment ratio reduces value here; it does not increase it.", isCorrect: false },
      { text: "$833,333", rationale: "That reflects the wrong operation and is not the assessed value.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 184,
    question: "An HO-6 policy is most commonly associated with:",
    answerOptions: [
      { text: "A condominium unit owner", rationale: "The HO-6 form is the homeowners policy most commonly associated with condominium ownership.", isCorrect: true },
      { text: "A tenant in an apartment rental", rationale: "That is more commonly associated with an HO-4 tenant-type policy.", isCorrect: false },
      { text: "A commercial warehouse operator", rationale: "Commercial insurance needs are different from an HO-6 homeowners policy.", isCorrect: false },
      { text: "A county assessor", rationale: "An assessor's job has nothing to do with an HO-6 form.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 185,
    question: "A New York planning board is most closely associated with:",
    answerOptions: [
      { text: "Subdivision and site-planning review", rationale: "Planning boards are typically involved in land development review such as subdivisions and site planning, rather than licensing or mortgage taxation.", isCorrect: true },
      { text: "Issuing salesperson licenses", rationale: "Licenses are regulated through the state, not the local planning board.", isCorrect: false },
      { text: "Approving dual agency disclosure forms", rationale: "Agency disclosure is not approved by a planning board.", isCorrect: false },
      { text: "Setting mortgage recording tax rates", rationale: "That is not a planning-board function.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 186,
    question: "In a New York condo transaction, a right of first refusal generally means:",
    answerOptions: [
      { text: "A designated party may match an outside offer before the sale proceeds", rationale: "A right of first refusal gives a named party the opportunity to match an outside offer before the owner completes the transaction with that outside buyer.", isCorrect: true },
      { text: "The buyer automatically gets financing first", rationale: "This right concerns transfer rights, not mortgage priority.", isCorrect: false },
      { text: "The seller must accept the first offer received", rationale: "The concept is about matching an offer, not taking the first offer automatically.", isCorrect: false },
      { text: "The county clerk records the deed before the contract is signed", rationale: "Recording sequence is unrelated to a right of first refusal.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 187,
    question: "If a New York broker advertises another broker's exclusive listing without permission, the main issue is:",
    answerOptions: [
      { text: "Lack of required authorization and proper identification", rationale: "Advertising another broker's exclusive listing generally requires authorization and proper disclosure to avoid misleading the public.", isCorrect: true },
      { text: "Failure to pay school taxes", rationale: "School taxes have nothing to do with the advertising issue described.", isCorrect: false },
      { text: "Automatic dual agency", rationale: "Advertising another broker's listing does not itself create dual agency.", isCorrect: false },
      { text: "Creation of a life estate", rationale: "A life estate is an ownership-interest concept, not an advertising consequence.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 188,
    question: "The main difference between a coop maintenance fee and a condo common charge is that a coop maintenance fee often includes:",
    answerOptions: [
      { text: "The shareholder's share of broader building obligations, sometimes including underlying debt and taxes", rationale: "Coop carrying charges often reflect the cooperative corporation's overall building obligations in a way that differs from condo common charges.", isCorrect: true },
      { text: "Only interior paint color choices", rationale: "Maintenance fees are financial obligations, not stylistic preferences.", isCorrect: false },
      { text: "The county clerk's deed recording fee", rationale: "A coop purchase does not typically turn on a deed-recording fee for the unit in the same way.", isCorrect: false },
      { text: "The state exam fee for the broker", rationale: "Licensing fees are unrelated to coop maintenance charges.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 189,
    question: "A latent defect in a New York transaction is best described as a defect that is:",
    answerOptions: [
      { text: "Hidden and not readily observable", rationale: "Latent defects are hidden conditions, as opposed to patent defects that are visible or readily discoverable.", isCorrect: true },
      { text: "Visible on ordinary inspection", rationale: "That describes a patent defect, not a latent one.", isCorrect: false },
      { text: "Guaranteed by title insurance", rationale: "Title insurance is not the definition of a latent defect.", isCorrect: false },
      { text: "Always unrelated to value", rationale: "Latent defects can absolutely affect value or safety.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 190,
    question: "The person most directly responsible for supervising unlicensed assistants in a New York brokerage is the:",
    answerOptions: [
      { text: "Licensed broker", rationale: "The licensed broker is responsible for supervision and control of activities within the brokerage, including unlicensed assistant activity.", isCorrect: true },
      { text: "County clerk", rationale: "The county clerk does not supervise brokerage staff.", isCorrect: false },
      { text: "Home inspector", rationale: "A home inspector is not the brokerage supervisor.", isCorrect: false },
      { text: "Buyer’s attorney", rationale: "An attorney may represent a party in the transaction, but does not supervise brokerage assistants.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 191,
    question: "How many hours of approved continuing education are generally required for renewal of a New York real estate salesperson license?",
    answerOptions: [
      { text: "22.5 hours", rationale: "New York generally requires 22.5 hours of approved continuing education for salesperson renewal, including mandated subject areas.", isCorrect: true },
      { text: "10 hours", rationale: "That is below the New York continuing-education requirement.", isCorrect: false },
      { text: "15 hours", rationale: "New York requires more than 15 hours for the standard renewal cycle.", isCorrect: false },
      { text: "30 hours", rationale: "That is not the standard New York salesperson continuing-education total.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 192,
    question: "A New York designated-sales-agent arrangement means:",
    answerOptions: [
      { text: "The broker remains a dual agent while separate sales agents are designated for buyer and seller", rationale: "Designated sales agency does not eliminate the brokerage's dual-agency status. It is still a dual-agency arrangement requiring informed written consent.", isCorrect: true },
      { text: "The broker ceases to represent either party", rationale: "The broker does not step out of the agency relationship.", isCorrect: false },
      { text: "No written consent is needed because the agents are different people", rationale: "Written informed consent is still required.", isCorrect: false },
      { text: "The transaction is converted into a lease rather than a sale", rationale: "Agency structure does not change the transaction type.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 193,
    question: "Which statement best describes a New York pocket card?",
    answerOptions: [
      { text: "A Department-issued identification card showing licensing information", rationale: "The New York pocket card is an official license-identification document showing the licensee's information and, for salespersons, associated broker information.", isCorrect: true },
      { text: "A buyer-agency disclosure summary carried by every customer", rationale: "Agency disclosure is separate from the pocket card.", isCorrect: false },
      { text: "A commission check issued after each closing", rationale: "The pocket card is not a payment instrument.", isCorrect: false },
      { text: "A title-insurance endorsement for co-op transactions", rationale: "Pocket cards are licensing documents, not title documents.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 194,
    question: "A New York business website that omits required brokerage disclosures on interior pages is most likely violating:",
    answerOptions: [
      { text: "Advertising rules", rationale: "New York web-advertising requirements are not limited to a homepage. Brokerage disclosures generally must appear where required throughout the site.", isCorrect: true },
      { text: "Property tax law", rationale: "Website brokerage identification is not a property tax issue.", isCorrect: false },
      { text: "Mortgage underwriting standards", rationale: "Underwriting standards concern lending, not advertising disclosure.", isCorrect: false },
      { text: "Condominium board rules", rationale: "A general brokerage site disclosure failure is not primarily a condo-board issue.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 195,
    question: "If a New York listing advertisement concerns another broker's exclusive listing, the advertising broker generally needs:",
    answerOptions: [
      { text: "Authorization from the listing broker", rationale: "Advertising another broker's exclusive listing generally requires authorization and proper identification to avoid misleading the public.", isCorrect: true },
      { text: "A deed from the property owner", rationale: "A deed is not required to advertise the listing.", isCorrect: false },
      { text: "Approval from the tax assessor", rationale: "The assessor is unrelated to listing-advertising permission.", isCorrect: false },
      { text: "Automatic MLS ownership rights", rationale: "MLS participation does not automatically create the right to advertise another broker's exclusive listing however one wishes.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 196,
    question: "A New York broker's record-retention duty is best understood as a requirement to keep covered records for:",
    answerOptions: [
      { text: "The legally required period, including in electronic form if retrievable", rationale: "The core rule is retention for the required period, and electronic storage is acceptable if the records remain accessible and compliant.", isCorrect: true },
      { text: "Only until closing day", rationale: "Closing does not end the duty to retain the required records.", isCorrect: false },
      { text: "Only while the broker remains in the same office", rationale: "Office location does not eliminate the retention obligation.", isCorrect: false },
      { text: "No fixed period if the transaction is all-cash", rationale: "The retention rule does not disappear just because financing was not involved.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 197,
    question: "A New York tax assessor's primary role is to:",
    answerOptions: [
      { text: "Determine assessed value for local property taxation", rationale: "The assessor's role is centered on property valuation for local tax-assessment purposes.", isCorrect: true },
      { text: "Issue real estate licenses", rationale: "Licensing is handled at the state level, not by the assessor.", isCorrect: false },
      { text: "Approve dual-agency consent forms", rationale: "Agency consent is not approved by the assessor.", isCorrect: false },
      { text: "Hold mortgage escrow deposits", rationale: "Escrow deposit handling is not the assessor's function.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 198,
    question: "A New York grievance board or board of assessment review most commonly deals with:",
    answerOptions: [
      { text: "Assessment challenges", rationale: "The grievance or assessment-review board is the usual first step in challenging a property assessment.", isCorrect: true },
      { text: "Dual-agency disputes between buyers and sellers", rationale: "Those are not handled by the assessment-review board.", isCorrect: false },
      { text: "Broker licensing exams", rationale: "Exam administration is unrelated to assessment review.", isCorrect: false },
      { text: "Issuing title insurance policies", rationale: "Title insurers do not sit as assessment-review boards.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 199,
    question: "If a New York property has a market value of $420,000 and is assessed at 75% of market value, the assessed value is:",
    answerOptions: [
      { text: "$315,000", rationale: "Assessed value equals market value multiplied by the assessment ratio. $420,000 x 0.75 = $315,000.", isCorrect: true },
      { text: "$345,000", rationale: "That is not 75 percent of $420,000.", isCorrect: false },
      { text: "$495,000", rationale: "Assessment here lowers value; it does not increase it.", isCorrect: false },
      { text: "$56,000", rationale: "That result reflects the wrong calculation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 200,
    question: "A New York property's NOI is $72,000 and the capitalization rate is 9%. The indicated value is:",
    answerOptions: [
      { text: "$800,000", rationale: "Value equals NOI divided by cap rate. $72,000 / 0.09 = $800,000.", isCorrect: true },
      { text: "$648,000", rationale: "That does not result from dividing NOI by the stated capitalization rate.", isCorrect: false },
      { text: "$79,200", rationale: "That is not the value indicated by the cap-rate formula.", isCorrect: false },
      { text: "$8,000,000", rationale: "That overstates value by shifting the decimal point incorrectly.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 201,
    question: "A New York mortgage broker is best described as someone who typically:",
    answerOptions: [
      { text: "Arranges loans between borrowers and lenders", rationale: "A mortgage broker generally arranges financing rather than funding loans in the broker's own name like a mortgage banker often does.", isCorrect: true },
      { text: "Issues deeds for condominium units", rationale: "Mortgage brokers do not issue deeds.", isCorrect: false },
      { text: "Sets county property-tax assessments", rationale: "Assessment is a tax-authority function, not a mortgage-broker function.", isCorrect: false },
      { text: "Approves cooperative board interviews", rationale: "Coop-board approval is not a mortgage-broker role.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 202,
    question: "RESPA is mainly concerned with:",
    answerOptions: [
      { text: "Settlement practices and restrictions on certain kickbacks and referral fees", rationale: "RESPA is a closing-and-settlement law focused on disclosures and prohibited referral-fee practices in covered transactions.", isCorrect: true },
      { text: "Issuing New York salesperson licenses", rationale: "RESPA is not a state real estate licensing law.", isCorrect: false },
      { text: "Setting condominium maintenance charges", rationale: "Condo charges are not governed by RESPA's core purpose.", isCorrect: false },
      { text: "Replacing title insurance with appraisal reports", rationale: "RESPA does not replace title insurance or appraisal functions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 203,
    question: "An HO-4 policy is most commonly associated with:",
    answerOptions: [
      { text: "A tenant or cooperative occupant form", rationale: "HO-4 is commonly associated with tenants and is often also discussed in connection with cooperative occupancy situations.", isCorrect: true },
      { text: "A condominium unit owner form only", rationale: "HO-6 is the form more commonly associated with condominium unit owners.", isCorrect: false },
      { text: "A vacant land investor only", rationale: "HO-4 is not the standard answer for vacant land ownership.", isCorrect: false },
      { text: "A county tax office", rationale: "Insurance forms do not describe tax-office functions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 204,
    question: "The National Flood Insurance Program is primarily relevant because standard property insurance often:",
    answerOptions: [
      { text: "Excludes flood damage", rationale: "Flood damage is commonly excluded from standard property policies, which is why separate flood coverage becomes important.", isCorrect: true },
      { text: "Includes every flood risk automatically", rationale: "That is the opposite of the usual problem.", isCorrect: false },
      { text: "Eliminates the need for hazard insurance", rationale: "Flood insurance does not replace other property coverage needs.", isCorrect: false },
      { text: "Requires no underwriting", rationale: "The existence of the program does not mean no underwriting or eligibility standards apply.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 205,
    question: "A property manager's operating budget is mainly used to:",
    answerOptions: [
      { text: "Project income and expenses for property operations", rationale: "An operating budget is a management and planning tool used to forecast performance and control operations.", isCorrect: true },
      { text: "Issue a deed to a new buyer", rationale: "Deed transfer is not the role of an operating budget.", isCorrect: false },
      { text: "Determine whether a zoning variance is required", rationale: "Zoning relief is a land-use matter, not a budgeting function.", isCorrect: false },
      { text: "Replace the annual tax bill", rationale: "Budgets account for taxes but do not replace tax obligations.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 206,
    question: "Preventive maintenance differs from emergency maintenance because preventive maintenance is:",
    answerOptions: [
      { text: "Scheduled to reduce the chance of future breakdowns", rationale: "Preventive maintenance is planned in advance, while emergency maintenance responds to urgent or unexpected conditions.", isCorrect: true },
      { text: "Performed only after a major failure", rationale: "That describes reactive or emergency work, not preventive maintenance.", isCorrect: false },
      { text: "Always less expensive than any repair", rationale: "Preventive maintenance is often cost-effective, but 'always' is too absolute.", isCorrect: false },
      { text: "Illegal unless approved by the planning board", rationale: "Routine maintenance is not generally a planning-board issue.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 207,
    question: "A New York salesperson who gives legal conclusions about contract enforceability risks:",
    answerOptions: [
      { text: "Unauthorized practice of law", rationale: "A licensee may explain process and business terms, but legal conclusions about rights and enforceability belong to attorneys.", isCorrect: true },
      { text: "Automatic licensure as an attorney", rationale: "Improper legal advice does not create attorney status.", isCorrect: false },
      { text: "Creating a tax exemption", rationale: "Tax exemptions are unrelated to unauthorized practice of law.", isCorrect: false },
      { text: "Converting the contract into a deed", rationale: "That is not the consequence of giving legal advice.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 208,
    question: "A New York broker who mixes client funds with business operating funds is committing:",
    answerOptions: [
      { text: "Commingling", rationale: "Commingling means mixing client or escrow money with the broker's own funds.", isCorrect: true },
      { text: "Capitalization", rationale: "Capitalization is a valuation concept, not a trust-account violation.", isCorrect: false },
      { text: "Appreciation", rationale: "Appreciation is an increase in value, not a fund-handling violation.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat is a government power when property passes without heirs.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 209,
    question: "A New York broker who uses client escrow money for personal or business expenses is committing:",
    answerOptions: [
      { text: "Conversion", rationale: "Conversion is the wrongful use of client funds and is more serious than simple commingling.", isCorrect: true },
      { text: "Variance", rationale: "A variance is zoning relief, not misuse of money.", isCorrect: false },
      { text: "Depreciation", rationale: "Depreciation is a valuation or tax concept, not a trust-fund violation.", isCorrect: false },
      { text: "Subrogation", rationale: "Subrogation is an insurance concept, not misuse of escrow money.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 210,
    question: "If a New York home sells for $850,000 and the total commission is 4%, the total commission is:",
    answerOptions: [
      { text: "$34,000", rationale: "Multiply the sale price by the commission rate. $850,000 x 0.04 = $34,000.", isCorrect: true },
      { text: "$28,000", rationale: "That would reflect the wrong percentage calculation.", isCorrect: false },
      { text: "$40,000", rationale: "That would be too high for a 4 percent commission.", isCorrect: false },
      { text: "$8,500", rationale: "That is only 1 percent of the sale price.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 211,
    question: "A New York fair housing notice is required to be displayed prominently in a broker office, on licensee websites, and:",
    answerOptions: [
      { text: "At open houses", rationale: "New York's fair housing display rules extend beyond the brokerage office and include websites and open houses.", isCorrect: true },
      { text: "Only in county clerk offices", rationale: "The county clerk is not the central display location for the brokerage fair housing notice.", isCorrect: false },
      { text: "Only inside cooperative board rooms", rationale: "The rule is not limited to cooperative buildings.", isCorrect: false },
      { text: "Only on tax-assessment appeals", rationale: "Fair housing notice posting is unrelated to assessment-appeal filings.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 212,
    question: "A New York licensee who discriminates in housing-related services based on a protected class risks violating:",
    answerOptions: [
      { text: "Fair housing law", rationale: "Housing discrimination on protected-class grounds is the core concern of fair housing law.", isCorrect: true },
      { text: "Only the mortgage recording tax rules", rationale: "Tax-recording rules do not define housing discrimination law.", isCorrect: false },
      { text: "Only title-insurance underwriting standards", rationale: "Title insurance is not the primary legal framework for fair housing violations.", isCorrect: false },
      { text: "Only the local building code", rationale: "Building codes regulate construction and safety, not protected-class discrimination in housing services.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 213,
    question: "A New York broker who fails to keep the Housing and Anti-Discrimination Disclosure Form for the required period risks a violation involving:",
    answerOptions: [
      { text: "Fair housing record retention", rationale: "New York imposes a retention obligation for this disclosure, so failing to keep it can create a compliance problem.", isCorrect: true },
      { text: "Failure to record a deed", rationale: "This issue concerns fair housing documentation, not deed recording.", isCorrect: false },
      { text: "Unpaid mortgage recording tax", rationale: "Mortgage recording tax is unrelated to fair housing-form retention.", isCorrect: false },
      { text: "Unauthorized building occupancy", rationale: "Occupancy approval is a different regulatory issue.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 214,
    question: "A New York title problem caused by an unreleased mortgage is best described as:",
    answerOptions: [
      { text: "A cloud on title", rationale: "An unreleased mortgage appearing in the public record can impair title and therefore create a cloud on title.", isCorrect: true },
      { text: "A capital gain", rationale: "Capital gain concerns tax profit on sale, not title defects.", isCorrect: false },
      { text: "A valid appraisal adjustment", rationale: "Title defects are not appraisal adjustments.", isCorrect: false },
      { text: "A zoning variance", rationale: "A variance is zoning relief, not a title defect.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 215,
    question: "A New York quitclaim deed differs from a general warranty deed because the quitclaim deed:",
    answerOptions: [
      { text: "Conveys whatever interest the grantor has without title warranties", rationale: "A quitclaim deed gives no broad warranty of title. It transfers whatever interest the grantor has, if any.", isCorrect: true },
      { text: "Always guarantees marketable title", rationale: "That is the opposite of the quitclaim concept.", isCorrect: false },
      { text: "Can be used only for cooperative apartments", rationale: "Quitclaim deeds are not limited to cooperative housing.", isCorrect: false },
      { text: "Transfers only personal property", rationale: "A deed concerns real property interests, not just personal property.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 216,
    question: "A lis pendens is significant in a New York transaction because it:",
    answerOptions: [
      { text: "Notifies the world that litigation may affect title or possession", rationale: "A lis pendens warns potential buyers and lenders that pending litigation may affect the property.", isCorrect: true },
      { text: "Replaces the need for a title search", rationale: "It is one record item among others and does not eliminate the need for proper title review.", isCorrect: false },
      { text: "Automatically clears a mortgage lien", rationale: "A lis pendens does not release liens.", isCorrect: false },
      { text: "Functions as a cooperative board approval", rationale: "It is a litigation notice, not a board-consent document.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 217,
    question: "A month-to-month tenancy in New York is most closely classified as:",
    answerOptions: [
      { text: "A periodic tenancy", rationale: "A month-to-month arrangement is the classic example of a periodic tenancy because it renews from period to period until properly terminated.", isCorrect: true },
      { text: "An estate for years", rationale: "An estate for years has a definite ending date rather than automatically renewing month to month.", isCorrect: false },
      { text: "A fee simple estate", rationale: "Fee simple is ownership, not a leasehold.", isCorrect: false },
      { text: "A tenancy by the entirety", rationale: "That is a marital ownership form, not a lease classification.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 218,
    question: "If a landlord's conduct makes leased premises unusable and the tenant leaves, the strongest exam concept is:",
    answerOptions: [
      { text: "Constructive eviction", rationale: "Constructive eviction occurs when conditions caused by the landlord effectively force the tenant out even without physical removal.", isCorrect: true },
      { text: "Joint tenancy", rationale: "Joint tenancy is a co-ownership concept, not a lease-remedy concept.", isCorrect: false },
      { text: "Specific performance", rationale: "Specific performance is a contract remedy, not the tenancy doctrine at issue here.", isCorrect: false },
      { text: "Subrogation", rationale: "Subrogation is generally an insurance concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 219,
    question: "A New York Certificate of Occupancy is most relevant because it shows:",
    answerOptions: [
      { text: "The building or space is approved for occupancy or use under applicable code requirements", rationale: "A Certificate of Occupancy relates to lawful building use and occupancy status, not agency, title insurance, or loan approval.", isCorrect: true },
      { text: "The borrower has been preapproved for a mortgage", rationale: "Preapproval is a lending concept, not an occupancy approval.", isCorrect: false },
      { text: "A dual-agency disclosure was signed", rationale: "Occupancy certificates have nothing to do with agency consent.", isCorrect: false },
      { text: "A title insurance premium has been paid", rationale: "Title insurance is separate from code and occupancy approval.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 220,
    question: "A New York building permit is most directly related to:",
    answerOptions: [
      { text: "Compliance with building and safety code requirements before or during work", rationale: "Building permits are used to authorize and regulate construction, alteration, or improvement work under applicable codes.", isCorrect: true },
      { text: "The seller's obligation to provide flood insurance", rationale: "Flood coverage is an insurance issue, not a permit issue.", isCorrect: false },
      { text: "The appraiser's final value conclusion", rationale: "Appraisal is separate from permit approval.", isCorrect: false },
      { text: "A broker's continuing education requirement", rationale: "Licensing education has nothing to do with building permits.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 221,
    question: "A New York planning board is most likely reviewing:",
    answerOptions: [
      { text: "Subdivision and site-planning matters", rationale: "Planning boards focus on land development and planning issues such as subdivision plats and site planning.", isCorrect: true },
      { text: "Salesperson exam scoring", rationale: "Exam administration is not a planning-board function.", isCorrect: false },
      { text: "Fair housing complaint penalties", rationale: "Planning boards do not serve as fair housing enforcement tribunals.", isCorrect: false },
      { text: "Escrow-account reconciliation", rationale: "Escrow reconciliation is not a planning-board role.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 222,
    question: "A New York property's gross rent multiplier is found by dividing:",
    answerOptions: [
      { text: "Sale price by gross rental income", rationale: "GRM is a quick screening tool found by dividing sale price by gross rent.", isCorrect: true },
      { text: "Gross rental income by sale price", rationale: "That reverses the GRM formula.", isCorrect: false },
      { text: "Net operating income by sale price", rationale: "NOI is used with capitalization concepts, not GRM in that form.", isCorrect: false },
      { text: "Sale price by assessed value", rationale: "That ratio is unrelated to gross rent multiplier.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 223,
    question: "A New York appraiser or student evaluating a newer special-purpose building may find the most useful approach is often the:",
    answerOptions: [
      { text: "Cost approach", rationale: "The cost approach is often especially helpful for newer or special-purpose properties where comparable sales may be limited.", isCorrect: true },
      { text: "Income approach only", rationale: "The income approach may matter, but it is not the standard best answer for a newer special-purpose structure.", isCorrect: false },
      { text: "Mortgage recording approach", rationale: "That is not a recognized appraisal approach.", isCorrect: false },
      { text: "Cease and desist approach", rationale: "That is unrelated to appraisal.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 224,
    question: "If a New York property has a gross annual rent of $36,000 and sells for $432,000, the GRM is:",
    answerOptions: [
      { text: "12", rationale: "GRM equals sale price divided by gross annual rent. $432,000 / $36,000 = 12.", isCorrect: true },
      { text: "8.33", rationale: "That does not result from dividing the sale price by gross annual rent.", isCorrect: false },
      { text: "0.12", rationale: "That reflects the wrong decimal relationship.", isCorrect: false },
      { text: "468,000", rationale: "That is not a multiplier at all.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 225,
    question: "A New York seller who has already paid annual taxes through year-end may receive a closing credit because taxes are:",
    answerOptions: [
      { text: "Prepaid items", rationale: "If the seller prepaid taxes covering a period after closing, the seller is generally credited and the buyer debited for that future benefit.", isCorrect: true },
      { text: "Accrued items only", rationale: "Accrued items are unpaid up to closing, which is the opposite situation.", isCorrect: false },
      { text: "Always ignored at closing", rationale: "Taxes are commonly prorated or adjusted at closing.", isCorrect: false },
      { text: "Converted automatically into commission", rationale: "Taxes are not transformed into commission.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 226,
    question: "If a New York buyer is charged an expense on the closing statement, that entry is usually a:",
    answerOptions: [
      { text: "Debit to the buyer", rationale: "A debit is an amount charged to a party on the closing statement.", isCorrect: true },
      { text: "Credit to the buyer", rationale: "A credit would be in the buyer's favor rather than a charge.", isCorrect: false },
      { text: "Debit to the seller", rationale: "That would charge the seller, not the buyer.", isCorrect: false },
      { text: "Tax exemption", rationale: "A debit is an accounting entry, not an exemption.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 227,
    question: "A New York seller who receives a favorable amount on the closing statement generally sees that amount as a:",
    answerOptions: [
      { text: "Credit to the seller", rationale: "A credit entry on a closing statement is in favor of the party receiving it.", isCorrect: true },
      { text: "Debit to the seller", rationale: "A debit is a charge, not a favorable entry.", isCorrect: false },
      { text: "Lender holdback", rationale: "A lender holdback is not the standard settlement-statement answer here.", isCorrect: false },
      { text: "Pocket card issuance", rationale: "Pocket cards are licensing documents, not closing-statement entries.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 228,
    question: "If a New York property owner installs new landscaping solely because it costs $20,000, an appraiser would not automatically assume the property's value increased by $20,000 because of the principle of:",
    answerOptions: [
      { text: "Contribution", rationale: "Contribution means a component's value depends on how much it adds to the whole property, not merely what it cost.", isCorrect: true },
      { text: "Escheat", rationale: "Escheat is a government succession concept, not an appraisal principle.", isCorrect: false },
      { text: "Condemnation", rationale: "Condemnation is an eminent-domain process, not a valuation principle.", isCorrect: false },
      { text: "Conversion", rationale: "Conversion is a misuse-of-funds concept in brokerage, not appraisal.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 229,
    question: "A New York property manager's rent roll is most useful because it summarizes:",
    answerOptions: [
      { text: "Units, tenants, rents, and occupancy-related lease information", rationale: "A rent roll is a key property-management and underwriting document showing occupancy and rental-income structure.", isCorrect: true },
      { text: "Only the owner's personal tax return", rationale: "A rent roll is not a personal tax document.", isCorrect: false },
      { text: "Only building-code violations", rationale: "Code issues may matter, but that is not the purpose of a rent roll.", isCorrect: false },
      { text: "Only title-insurance exceptions", rationale: "Title exceptions are unrelated to a rent roll's normal use.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 230,
    question: "A New York licensee's personal-safety protocol for showings is most concerned with:",
    answerOptions: [
      { text: "Reducing field risk through preparation, communication, and awareness", rationale: "The safety unit is about practical risk reduction in a profession that involves meeting strangers and entering property.", isCorrect: true },
      { text: "Replacing the need for fair housing compliance", rationale: "Safety rules do not replace legal compliance duties.", isCorrect: false },
      { text: "Eliminating all property-insurance requirements", rationale: "Insurance is unrelated to the core showing-safety purpose.", isCorrect: false },
      { text: "Avoiding all written records of appointments", rationale: "Good safety practice often includes better documentation and communication, not less.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 231,
    question: "Article 12-A of the New York Real Property Law primarily governs:",
    answerOptions: [
      { text: "Licensing and regulation of real estate brokers and salespersons", rationale: "Article 12-A is the core statutory framework for New York real estate licensing and practice regulation.", isCorrect: true },
      { text: "Federal lead-based paint disclosure", rationale: "Lead-based paint disclosure arises from federal law, not Article 12-A.", isCorrect: false },
      { text: "County property-tax assessment appeals only", rationale: "Assessment appeals are not the primary subject of Article 12-A.", isCorrect: false },
      { text: "Only cooperative board interview procedures", rationale: "Coop board procedures are not the focus of Article 12-A.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 232,
    question: "A New York salesperson who changes brokers generally cannot lawfully continue practicing until:",
    answerOptions: [
      { text: "The new association is properly reflected through the licensing process", rationale: "Because a salesperson must work under an associated broker, broker changes must be properly handled through the licensing framework.", isCorrect: true },
      { text: "The salesperson joins a national REALTOR committee", rationale: "Association membership does not replace the licensing association requirement.", isCorrect: false },
      { text: "The county clerk records the salesperson's business card", rationale: "The county clerk does not govern salesperson-broker association in that way.", isCorrect: false },
      { text: "The previous broker agrees to split future commissions forever", rationale: "Compensation arrangements do not substitute for proper licensing status.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 233,
    question: "A New York dual agent with designated sales agents still owes the parties the limitation that comes with:",
    answerOptions: [
      { text: "Dual agency", rationale: "Even with designated sales agents, the brokerage remains in a dual-agency position and cannot provide undivided loyalty in the same way as separate firms.", isCorrect: true },
      { text: "Single agency only", rationale: "The structure does not convert the brokerage into pure single agency on both sides.", isCorrect: false },
      { text: "No agency at all", rationale: "The transaction still involves agency relationships.", isCorrect: false },
      { text: "Automatic buyer brokerage exclusivity", rationale: "Designated agency does not create automatic exclusive buyer representation beyond the disclosed arrangement.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 234,
    question: "Which statement best reflects a New York agent's duty to a customer who is not a client?",
    answerOptions: [
      { text: "The agent still owes honest and fair dealing", rationale: "A customer does not receive full fiduciary representation as a client, but honest dealing and lawful conduct are still required.", isCorrect: true },
      { text: "The agent may conceal known material defects", rationale: "A licensee may not lawfully conceal known material facts.", isCorrect: false },
      { text: "The agent may disclose false square footage if no contract is signed", rationale: "Misrepresentation is not excused just because a contract has not yet been signed.", isCorrect: false },
      { text: "The agent owes no legal duties at all", rationale: "That is incorrect; lawful, honest dealing still applies.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 235,
    question: "A New York property owner in a cease and desist zone who appears on the list is protected primarily from:",
    answerOptions: [
      { text: "Prohibited real estate solicitation", rationale: "Cease and desist zones are focused on limiting prohibited solicitation of listed homeowners.", isCorrect: true },
      { text: "Annual property taxes", rationale: "Cease and desist rules do not eliminate tax obligations.", isCorrect: false },
      { text: "Cooperative maintenance fees", rationale: "Those are ownership or occupancy costs, not the subject of cease and desist protection.", isCorrect: false },
      { text: "Lead-based paint disclosure", rationale: "Disclosure laws remain separate from cease and desist solicitation rules.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 236,
    question: "A New York office sign requirement is mainly intended to ensure:",
    answerOptions: [
      { text: "The brokerage is identifiable to the public at its registered office", rationale: "Office-sign rules are meant to identify the brokerage and support transparency and lawful operation of the office.", isCorrect: true },
      { text: "The broker can avoid maintaining records", rationale: "A sign requirement does not remove recordkeeping duties.", isCorrect: false },
      { text: "The salesperson may practice from any unregistered location", rationale: "The office-sign rule does not authorize unregistered practice locations.", isCorrect: false },
      { text: "The office becomes exempt from fair housing notice rules", rationale: "Fair housing notice obligations still apply.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 237,
    question: "A New York licensee using the phrase 'adults only' in a rental ad is most likely creating a problem under:",
    answerOptions: [
      { text: "Fair housing law", rationale: "Advertising language that suggests exclusion of families with children can create fair housing issues involving familial status.", isCorrect: true },
      { text: "Only mortgage brokerage law", rationale: "Mortgage brokerage law is not the core issue in discriminatory ad wording.", isCorrect: false },
      { text: "Only title recording law", rationale: "Recording law is unrelated to ad discrimination.", isCorrect: false },
      { text: "Only appraisal standards", rationale: "Appraisal standards do not govern fair housing ad language.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 238,
    question: "A New York Housing and Anti-Discrimination Disclosure Form requirement is strongest evidence that the state wants consumers to receive fair housing information:",
    answerOptions: [
      { text: "Early in the relationship, not only at contract time", rationale: "The timing requirement at first substantive contact shows that the information is intended to be delivered early enough to matter.", isCorrect: true },
      { text: "Only after closing", rationale: "That would defeat the disclosure's consumer-protection purpose.", isCorrect: false },
      { text: "Only if a commission dispute arises", rationale: "The form is not tied to commission disputes.", isCorrect: false },
      { text: "Only in all-cash transactions", rationale: "The obligation is not limited to cash deals.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 239,
    question: "If a New York condo unit owner receives title to the unit, that ownership is most consistent with:",
    answerOptions: [
      { text: "Deeded real property ownership", rationale: "Condominium ownership is generally deeded ownership of the unit plus a common-element interest.", isCorrect: true },
      { text: "Only shares in a corporation", rationale: "That is more characteristic of cooperative ownership.", isCorrect: false },
      { text: "A tenancy at will", rationale: "That is a leasehold concept, not condo ownership.", isCorrect: false },
      { text: "A mortgage commitment only", rationale: "Financing approval is not the ownership interest itself.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 240,
    question: "A New York cooperative maintenance fee is most likely associated with:",
    answerOptions: [
      { text: "Building-wide obligations and operating costs", rationale: "Coop maintenance fees commonly reflect building expenses and other shared obligations of the cooperative corporation.", isCorrect: true },
      { text: "Only title insurance premiums", rationale: "Maintenance is broader than title-insurance cost.", isCorrect: false },
      { text: "Only deed recording taxes", rationale: "Those are not the normal defining component of coop maintenance.", isCorrect: false },
      { text: "Only the salesperson exam filing fee", rationale: "Licensing fees are unrelated to coop carrying charges.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 241,
    question: "A New York owner who purchases a property with an annual NOI of $90,000 and a cap rate of 10% would estimate value at:",
    answerOptions: [
      { text: "$900,000", rationale: "Value equals NOI divided by cap rate. $90,000 / 0.10 = $900,000.", isCorrect: true },
      { text: "$90,000", rationale: "That is the NOI itself, not the indicated value.", isCorrect: false },
      { text: "$9,000,000", rationale: "That overstates value by shifting the decimal point incorrectly.", isCorrect: false },
      { text: "$810,000", rationale: "That does not reflect dividing NOI by the stated cap rate.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 242,
    question: "Depreciation for income tax purposes in real estate generally does not apply to:",
    answerOptions: [
      { text: "Land", rationale: "Land is not depreciated for income tax purposes because it is not treated as wearing out like improvements.", isCorrect: true },
      { text: "Qualifying building improvements", rationale: "Building improvements are the classic depreciable real estate asset.", isCorrect: false },
      { text: "Certain depreciable structures", rationale: "Structures may be depreciated subject to tax rules.", isCorrect: false },
      { text: "Eligible improvements with useful life allocation", rationale: "That phrasing describes items that can fall within depreciation treatment.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 243,
    question: "A Section 1031 exchange is most associated with:",
    answerOptions: [
      { text: "Deferring recognition of gain on qualifying investment or business property exchanges", rationale: "The key tested concept is deferral of gain, not permanent tax elimination.", isCorrect: true },
      { text: "Eliminating all future property taxes forever", rationale: "A 1031 exchange does not erase future taxation in that way.", isCorrect: false },
      { text: "Replacing a deed with a lease", rationale: "An exchange is not a lease substitution device.", isCorrect: false },
      { text: "Avoiding all title defects automatically", rationale: "Tax deferral has nothing to do with curing title problems.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 244,
    question: "A New York owner's adjusted basis is generally important because it affects the calculation of:",
    answerOptions: [
      { text: "Gain or loss on sale", rationale: "Adjusted basis is central to determining taxable gain or loss when property is sold.", isCorrect: true },
      { text: "The office sign wording", rationale: "Office-sign compliance is unrelated to tax basis.", isCorrect: false },
      { text: "The broker's pocket card", rationale: "Licensing identification is unrelated to adjusted basis.", isCorrect: false },
      { text: "The zoning board's meeting schedule", rationale: "Zoning procedures do not depend on tax basis.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 245,
    question: "A New York buyer comparing replacement cost and actual cash value coverage should understand that replacement cost generally:",
    answerOptions: [
      { text: "Does not reduce coverage by depreciation in the same way actual cash value does", rationale: "Replacement-cost coverage is typically broader than actual cash value because depreciation is not treated the same way.", isCorrect: true },
      { text: "Always pays exactly the mortgage balance", rationale: "Coverage valuation is not defined by the loan balance.", isCorrect: false },
      { text: "Always costs less than actual cash value coverage", rationale: "Replacement-cost coverage is not typically the cheaper form.", isCorrect: false },
      { text: "Eliminates the need for deductibles", rationale: "Deductibles may still apply.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 246,
    question: "A New York owner's liability coverage is most relevant if:",
    answerOptions: [
      { text: "Someone claims bodily injury or property damage caused by the insured's legal responsibility", rationale: "Liability coverage addresses claims by others for injuries or damages for which the insured may be responsible.", isCorrect: true },
      { text: "The owner wants to estimate NOI", rationale: "NOI is an investment analysis issue, not a liability-insurance issue.", isCorrect: false },
      { text: "The owner needs a legal description for a survey", rationale: "Survey description is unrelated to liability coverage.", isCorrect: false },
      { text: "The owner is calculating a capitalization rate", rationale: "Cap-rate analysis is not an insurance-liability concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 247,
    question: "A New York landlord-tenant dispute involving visible mold and water intrusion is most likely relevant on the exam as:",
    answerOptions: [
      { text: "A possible material condition or habitability issue", rationale: "Mold and moisture intrusion can raise condition and habitability concerns that may materially affect the property.", isCorrect: true },
      { text: "Proof that title is automatically marketable", rationale: "Condition issues do not prove clear title.", isCorrect: false },
      { text: "A guaranteed tax deduction", rationale: "Condition problems do not automatically create tax deductions.", isCorrect: false },
      { text: "A replacement for a lead-based paint disclosure", rationale: "Different disclosure and condition concepts do not replace one another.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 248,
    question: "A New York zoning board of appeals is most likely to hear a request for:",
    answerOptions: [
      { text: "Relief from zoning restrictions", rationale: "The zoning board of appeals is the local body commonly associated with variance and zoning-relief requests.", isCorrect: true },
      { text: "Renewal of a salesperson license", rationale: "Licensing renewal is handled through the state, not the zoning board.", isCorrect: false },
      { text: "A mortgage commitment extension", rationale: "Mortgage commitment timing is a lending issue, not zoning relief.", isCorrect: false },
      { text: "A title insurance endorsement", rationale: "Title endorsements are not issued by zoning boards.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 249,
    question: "If a New York property produces $120,000 in gross income and has $45,000 in operating expenses, the NOI is:",
    answerOptions: [
      { text: "$75,000", rationale: "NOI equals gross income minus operating expenses. $120,000 - $45,000 = $75,000.", isCorrect: true },
      { text: "$165,000", rationale: "That incorrectly adds income and expenses rather than subtracting expenses.", isCorrect: false },
      { text: "$45,000", rationale: "That is the expense figure, not the resulting NOI.", isCorrect: false },
      { text: "$120,000", rationale: "That ignores operating expenses entirely.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 250,
    question: "If a New York borrower's loan amount is $315,000 and the loan is 90% of value, the value or purchase price is:",
    answerOptions: [
      { text: "$350,000", rationale: "Value equals loan amount divided by LTV. $315,000 / 0.90 = $350,000.", isCorrect: true },
      { text: "$283,500", rationale: "That would be 90 percent of $315,000, which is the wrong operation.", isCorrect: false },
      { text: "$405,000", rationale: "That does not result from dividing by the stated LTV.", isCorrect: false },
      { text: "$31,500", rationale: "That is far too low and reflects a decimal error.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 251,
    question: "A New York salesperson who fails to complete a required continuing education obligation before renewal most likely risks:",
    answerOptions: [
      { text: "An inability to renew on time", rationale: "Continuing education is tied to license renewal eligibility. Failure to complete it can block or delay renewal.", isCorrect: true },
      { text: "Automatic conversion to broker status", rationale: "Education deficiencies do not elevate a salesperson to broker.", isCorrect: false },
      { text: "Automatic appointment to the licensing board", rationale: "Licensing boards are not populated through renewal noncompliance.", isCorrect: false },
      { text: "Permanent exemption from future education", rationale: "Noncompliance does not create an exemption.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 252,
    question: "A New York broker reviewing office procedures to reduce disciplinary risk should place the greatest emphasis on:",
    answerOptions: [
      { text: "Supervision and compliance controls", rationale: "The exam commonly links broker responsibility with oversight, supervision, and internal procedures that support lawful practice.", isCorrect: true },
      { text: "Increasing listing prices in every market", rationale: "Pricing strategy is not the core disciplinary-risk control issue here.", isCorrect: false },
      { text: "Avoiding all written policies", rationale: "Lack of policy structure increases risk rather than reducing it.", isCorrect: false },
      { text: "Refusing to keep transaction files", rationale: "Poor records can create major compliance problems.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 253,
    question: "Which action best reflects sound New York licensee safety practice at a first meeting with an unknown prospect?",
    answerOptions: [
      { text: "Verify identity and maintain office awareness of the appointment", rationale: "Safety questions usually test commonsense field practices such as documenting appointments and verifying who the prospect is.", isCorrect: true },
      { text: "Meet in a vacant building without telling anyone", rationale: "That increases risk and contradicts basic safety guidance.", isCorrect: false },
      { text: "Hand over office keys immediately", rationale: "Providing unsupervised access is not a sound initial safety measure.", isCorrect: false },
      { text: "Accept cash from the prospect before introductions", rationale: "That is not the primary safety best practice and raises other issues.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 254,
    question: "A New York broker's transaction file is most important because it helps document:",
    answerOptions: [
      { text: "Compliance with brokerage obligations", rationale: "Records support proof of proper handling, disclosures, agreements, and other actions relevant to compliance and supervision.", isCorrect: true },
      { text: "The buyer's private tax return", rationale: "A brokerage file is not maintained for that purpose.", isCorrect: false },
      { text: "The zoning board's election results", rationale: "That has nothing to do with brokerage recordkeeping.", isCorrect: false },
      { text: "A lender's internal employee handbook", rationale: "Internal lender manuals are outside the brokerage's core file purpose.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 255,
    question: "A New York mortgage broker most directly acts as:",
    answerOptions: [
      { text: "An intermediary helping place borrowers with lenders", rationale: "Mortgage brokerage questions usually center on arranging or facilitating mortgage loans rather than directly serving as the lender.", isCorrect: true },
      { text: "A zoning official issuing use variances", rationale: "Zoning relief is unrelated to mortgage brokerage.", isCorrect: false },
      { text: "A title closer guaranteeing marketable title", rationale: "Mortgage brokers do not guarantee title.", isCorrect: false },
      { text: "A county assessor setting tax rates", rationale: "Assessment and taxation are separate local-government functions.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 256,
    question: "In New York, a tenant who remains in possession after a lease ends and continues paying rent that the landlord accepts may create:",
    answerOptions: [
      { text: "A holdover tenancy or periodic tenancy by implication", rationale: "If the landlord accepts rent after expiration, the law may imply a continuing tenancy rather than a complete termination of occupancy rights.", isCorrect: true },
      { text: "Fee simple absolute ownership", rationale: "Remaining after lease expiration does not create ownership.", isCorrect: false },
      { text: "A cooperative proprietary lease automatically", rationale: "Coop occupancy rights arise through a different ownership structure.", isCorrect: false },
      { text: "A guaranteed option to purchase", rationale: "A holdover situation does not itself create purchase rights.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 257,
    question: "A New York seller who knows a foundation wall has shifted but paints over the crack instead of revealing it is most likely concealing:",
    answerOptions: [
      { text: "A material defect", rationale: "A concealed structural issue is the type of defect that can materially affect value, safety, or desirability.", isCorrect: true },
      { text: "A lot-and-block description", rationale: "Legal descriptions are unrelated to concealed condition problems.", isCorrect: false },
      { text: "A mortgage satisfaction", rationale: "A satisfaction concerns lien release, not a hidden structural issue.", isCorrect: false },
      { text: "A broker's pocket card", rationale: "Licensing identification has nothing to do with physical property defects.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 258,
    question: "A special assessment imposed for a new sidewalk is best described as:",
    answerOptions: [
      { text: "A charge for a specific public improvement benefiting property", rationale: "Special assessments are commonly linked to particular public improvements that benefit identified properties.", isCorrect: true },
      { text: "A broker commission split", rationale: "Broker compensation is unrelated to public improvement assessments.", isCorrect: false },
      { text: "A federal income tax deduction", rationale: "An assessment is a local charge, not itself a federal deduction concept.", isCorrect: false },
      { text: "A mortgage prepayment penalty", rationale: "Loan-prepayment provisions are unrelated to municipal assessments.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 259,
    question: "An appraiser who gives the greatest weight to nearby recent sales of similar New York homes is primarily using the:",
    answerOptions: [
      { text: "Sales comparison approach", rationale: "For typical one-to-four family owner-occupied property, the sales comparison approach is usually the most directly relevant approach.", isCorrect: true },
      { text: "Income capitalization approach", rationale: "That is generally more important for income-producing property.", isCorrect: false },
      { text: "Gross multiplier tax approach", rationale: "That is not a standard named appraisal approach.", isCorrect: false },
      { text: "Replacement note approach", rationale: "That is not one of the three classic appraisal approaches.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 260,
    question: "A deed that conveys whatever interest the grantor has, if any, but makes no title warranties is a:",
    answerOptions: [
      { text: "Quitclaim deed", rationale: "A quitclaim deed transfers the grantor's present interest, if any, without promising good title.", isCorrect: true },
      { text: "General warranty deed", rationale: "A general warranty deed provides broad title covenants.", isCorrect: false },
      { text: "Full covenant deed", rationale: "That implies warranty protection rather than no-warranty conveyance.", isCorrect: false },
      { text: "Purchase-money mortgage", rationale: "A mortgage secures debt; it is not a deed.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 261,
    question: "A New York broker training new salespersons on file retention should emphasize that records are valuable because they can:",
    answerOptions: [
      { text: "Help show what disclosures and agreements were actually handled", rationale: "Recordkeeping is practical evidence of what occurred in the transaction and is central to compliance review.", isCorrect: true },
      { text: "Replace the need for a written contract", rationale: "Good files do not eliminate contract-law requirements.", isCorrect: false },
      { text: "Guarantee that every closing will occur", rationale: "Files document activity but do not guarantee outcomes.", isCorrect: false },
      { text: "Eliminate all fair housing risk automatically", rationale: "Records help, but they do not erase substantive legal risk.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 262,
    question: "Which action by a New York licensee would most likely raise a professional-practice concern?",
    answerOptions: [
      { text: "Making statements about a property's condition without a reasonable basis", rationale: "Professional-practice questions often test the need for accuracy, competence, and avoiding careless or misleading statements.", isCorrect: true },
      { text: "Keeping client confidences", rationale: "That is consistent with proper practice.", isCorrect: false },
      { text: "Presenting offers promptly", rationale: "That reflects normal brokerage duties.", isCorrect: false },
      { text: "Using written disclosures", rationale: "Written disclosures usually support compliance rather than undermine it.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 263,
    question: "A New York landlord most likely wants a security deposit primarily to:",
    answerOptions: [
      { text: "Provide protection against certain tenant defaults or damages", rationale: "Security deposits are generally used to protect the landlord against unpaid obligations or property damage, subject to law.", isCorrect: true },
      { text: "Transfer fee title to the tenant", rationale: "A security deposit does not convey ownership.", isCorrect: false },
      { text: "Create an automatic tax exemption", rationale: "Deposits do not create tax exemptions.", isCorrect: false },
      { text: "Substitute for all lease terms", rationale: "A deposit does not replace the lease agreement.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 264,
    question: "An assessed value of $240,000 with a tax rate of 2.5% would produce annual property taxes of:",
    answerOptions: [
      { text: "$6,000", rationale: "Annual tax equals assessed value times tax rate. $240,000 x 0.025 = $6,000.", isCorrect: true },
      { text: "$2,400", rationale: "That uses the wrong percentage calculation.", isCorrect: false },
      { text: "$9,600", rationale: "That overstates the result substantially.", isCorrect: false },
      { text: "$24,000", rationale: "That reflects a decimal-place error.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 265,
    question: "A New York buyer reviewing closing documents sees a deed, transfer tax items, and recording charges. These are most closely associated with:",
    answerOptions: [
      { text: "Title transfer and closing", rationale: "Deeds, recording, and transfer charges are classic closing and conveyance topics tied to transferring title.", isCorrect: true },
      { text: "Only property management operations", rationale: "These are not ordinary property-management functions.", isCorrect: false },
      { text: "Appraisal licensing", rationale: "Appraisal licensing is unrelated to deed transfer charges.", isCorrect: false },
      { text: "Coop board election procedures", rationale: "Board procedures do not define standard deed closing items.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 266,
    question: "A New York mortgage broker fee should most likely be understood by a borrower as:",
    answerOptions: [
      { text: "Compensation for arranging or helping obtain financing", rationale: "Mortgage broker compensation is typically tied to helping place or arrange a loan rather than insuring property or paying taxes.", isCorrect: true },
      { text: "A mandatory property-tax refund", rationale: "Mortgage broker fees are not tax refunds.", isCorrect: false },
      { text: "A substitute for hazard insurance", rationale: "Loan-arrangement compensation does not replace insurance coverage.", isCorrect: false },
      { text: "A zoning-map filing charge", rationale: "Zoning filings are unrelated to mortgage broker compensation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 267,
    question: "A New York homeowner compares recent sales of three similar nearby homes to estimate asking price. This is most similar to:",
    answerOptions: [
      { text: "A comparative market analysis", rationale: "Using recent comparable sales to estimate likely market price is the essence of a CMA.", isCorrect: true },
      { text: "A title abstract", rationale: "An abstract summarizes title history, not market pricing.", isCorrect: false },
      { text: "A mortgage amortization schedule", rationale: "Amortization tracks loan repayment over time.", isCorrect: false },
      { text: "A deed covenant", rationale: "A deed covenant is a title promise, not a pricing analysis.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 268,
    question: "A New York listing broker who receives a written offer while the seller is unavailable should generally:",
    answerOptions: [
      { text: "Follow prior lawful seller instructions and established brokerage procedure", rationale: "Professional-practice questions often focus on lawful handling of offers, seller instructions, and avoiding unauthorized action.", isCorrect: true },
      { text: "Destroy the offer if it seems low", rationale: "A licensee should not suppress offers based on personal judgment.", isCorrect: false },
      { text: "Accept the offer on behalf of the seller without authority", rationale: "Acceptance without authority exceeds the broker's role.", isCorrect: false },
      { text: "Disclose the buyer's maximum price to competing buyers", rationale: "That would raise confidentiality and ethical concerns.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 269,
    question: "A New York rental applicant's income, credit, and landlord references are most relevant to a landlord's decision about:",
    answerOptions: [
      { text: "Whether the applicant appears likely to perform lease obligations", rationale: "Rental screening commonly focuses on the applicant's apparent ability and likelihood to meet lease obligations.", isCorrect: true },
      { text: "Whether a deed should include full covenants", rationale: "Lease screening is unrelated to deed warranty choices.", isCorrect: false },
      { text: "Whether the tax assessor should change the mill rate", rationale: "Assessment rates are not set through a tenant-screening process.", isCorrect: false },
      { text: "Whether the zoning board should grant a variance", rationale: "Zoning relief is unrelated to applicant credit review.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 270,
    question: "If a New York property has effective gross income of $180,000, operating expenses of $70,000, and a cap rate of 8%, the indicated value is:",
    answerOptions: [
      { text: "$1,375,000", rationale: "First calculate NOI: $180,000 - $70,000 = $110,000. Then divide by 0.08 to get $1,375,000.", isCorrect: true },
      { text: "$880,000", rationale: "That does not result from dividing the NOI by the stated cap rate.", isCorrect: false },
      { text: "$1,440,000", rationale: "That reflects using the wrong income figure or math.", isCorrect: false },
      { text: "$110,000", rationale: "That is the NOI, not the capitalized value.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 271,
    question: "A New York property manager preparing an operating budget is most directly concerned with estimating:",
    answerOptions: [
      { text: "Income and operating expenses for the property", rationale: "Property management budgeting centers on projected income, expenses, and ongoing property operations.", isCorrect: true },
      { text: "The broker's licensing exam score", rationale: "Licensing exams are unrelated to property operating budgets.", isCorrect: false },
      { text: "The county clerk's recording sequence", rationale: "Recording office workflow is not the purpose of a management budget.", isCorrect: false },
      { text: "The zoning board's election cycle", rationale: "Local board schedules do not define property operating budgets.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 272,
    question: "A New York property manager is most likely to recommend preventive maintenance because it can:",
    answerOptions: [
      { text: "Reduce costly future repairs and preserve property condition", rationale: "Preventive maintenance is commonly tested as a way to control long-term costs and protect the property's usefulness and value.", isCorrect: true },
      { text: "Eliminate all property taxes", rationale: "Maintenance does not eliminate tax obligations.", isCorrect: false },
      { text: "Replace the need for leases", rationale: "Maintenance planning does not replace rental agreements.", isCorrect: false },
      { text: "Convert a tenant into a shareholder", rationale: "That would relate more to cooperative ownership, not routine management.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 273,
    question: "A New York investor's taxable gain on sale is generally affected by the property's:",
    answerOptions: [
      { text: "Adjusted basis", rationale: "Adjusted basis is a core tax concept because gain or loss is generally measured against it.", isCorrect: true },
      { text: "Open-house sign color", rationale: "Marketing choices do not determine taxable gain.", isCorrect: false },
      { text: "MLS photo order", rationale: "Photo sequence has no role in basis calculation.", isCorrect: false },
      { text: "Lockbox brand", rationale: "Equipment brand is unrelated to gain computation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 274,
    question: "Depreciation deductions in real estate are generally associated with:",
    answerOptions: [
      { text: "Improvements rather than land", rationale: "The commonly tested rule is that land is not depreciated, while qualifying improvements may be.", isCorrect: true },
      { text: "Raw land only", rationale: "Land generally is not the depreciable component.", isCorrect: false },
      { text: "The broker's commission statement", rationale: "A commission statement is not the depreciable asset.", isCorrect: false },
      { text: "The deed recording tax only", rationale: "Recording taxes are not the core depreciation concept.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 275,
    question: "A New York licensee checking renewal requirements should be most alert to whether the license is:",
    answerOptions: [
      { text: "Current and eligible for timely renewal", rationale: "Renewal questions focus on status, timing, and whether the licensee has satisfied renewal prerequisites.", isCorrect: true },
      { text: "Listed in the local newspaper", rationale: "Newspaper publication is not the key renewal issue.", isCorrect: false },
      { text: "Signed by the county assessor", rationale: "The assessor is not the relevant licensing authority.", isCorrect: false },
      { text: "Recorded with the county clerk", rationale: "Licenses are not renewed through deed-recording procedures.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 276,
    question: "A New York broker conducting a safety policy review should most likely require salespersons to:",
    answerOptions: [
      { text: "Share appointment details with the office before isolated showings", rationale: "Safety procedures often include documenting where agents will be and who they are meeting before field showings.", isCorrect: true },
      { text: "Turn off their phone location services and say nothing", rationale: "That reduces rather than improves safety.", isCorrect: false },
      { text: "Meet unknown clients only after dark", rationale: "That increases risk.", isCorrect: false },
      { text: "Avoid confirming a prospect's identity", rationale: "Identity verification is a standard safety step.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 277,
    question: "A New York office audit would most likely examine whether required transaction records were:",
    answerOptions: [
      { text: "Maintained and organized properly", rationale: "Recordkeeping questions emphasize retention, organization, and the brokerage's ability to demonstrate what occurred in a transaction.", isCorrect: true },
      { text: "Printed on colored paper", rationale: "Paper color is not the key compliance issue.", isCorrect: false },
      { text: "Signed by the zoning board", rationale: "Zoning boards do not supervise brokerage record files.", isCorrect: false },
      { text: "Approved by neighboring brokers", rationale: "Other brokers do not validate a brokerage's records for compliance.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 278,
    question: "A New York mortgage broker who quotes loan terms to a borrower should be understood as dealing primarily with:",
    answerOptions: [
      { text: "Financing arrangements", rationale: "Mortgage brokerage work centers on financing placement and loan-arrangement terms rather than title or land-use administration.", isCorrect: true },
      { text: "Subdivision approval", rationale: "Subdivision approval is a land-use matter.", isCorrect: false },
      { text: "Title covenants", rationale: "Title covenants are deed promises, not loan-arrangement terms.", isCorrect: false },
      { text: "Property-tax exemptions", rationale: "Tax exemptions are separate from mortgage brokerage services.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 279,
    question: "A New York lease clause requiring the tenant to follow building rules is most closely tied to the landlord's interest in:",
    answerOptions: [
      { text: "Orderly occupancy and property management", rationale: "Building rules support the landlord's management of common operations, safety, and tenant conduct.", isCorrect: true },
      { text: "Conveying fee title", rationale: "Lease rules do not transfer ownership.", isCorrect: false },
      { text: "Changing the zoning map", rationale: "Lease rules are not zoning amendments.", isCorrect: false },
      { text: "Recording a mortgage satisfaction", rationale: "Lien release is unrelated to tenant house rules.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 280,
    question: "A New York seller who covers up recurring seepage rather than disclosing it most likely creates an issue involving:",
    answerOptions: [
      { text: "Concealment of a material condition", rationale: "Recurring seepage can be material to a buyer, and concealment raises disclosure and condition issues.", isCorrect: true },
      { text: "A lawful title curative measure", rationale: "Physical concealment does not cure title problems.", isCorrect: false },
      { text: "A mortgage assignment", rationale: "Assignment concerns transfer of loan rights, not hidden water issues.", isCorrect: false },
      { text: "A CMA adjustment", rationale: "Comparative pricing analysis is unrelated to concealment.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 281,
    question: "A New York property manager hired for an apartment building is most likely responsible for:",
    answerOptions: [
      { text: "Coordinating maintenance, tenant matters, and day-to-day operations", rationale: "Property managers commonly handle ongoing operational issues rather than licensing, land-use approval, or title insurance.", isCorrect: true },
      { text: "Issuing broker licenses", rationale: "Licensing is handled by the state.", isCorrect: false },
      { text: "Approving subdivision plats", rationale: "Subdivision approvals are handled by local land-use authorities.", isCorrect: false },
      { text: "Guaranteeing title insurance coverage", rationale: "Title insurance is not guaranteed by the property manager.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 282,
    question: "A New York investor uses a like-kind exchange primarily to seek:",
    answerOptions: [
      { text: "Deferral of recognized gain", rationale: "A like-kind exchange question typically tests deferral, not elimination of all taxes or non-tax issues.", isCorrect: true },
      { text: "Automatic removal of all liens", rationale: "Tax deferral does not automatically clear liens.", isCorrect: false },
      { text: "A waiver of zoning restrictions", rationale: "Zoning rules are unrelated to like-kind exchange treatment.", isCorrect: false },
      { text: "Permanent exemption from assessments", rationale: "Local assessments are not erased by a tax-deferred exchange.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 283,
    question: "A New York salesperson completes required education but misses the renewal filing deadline. The main issue is most likely:",
    answerOptions: [
      { text: "Timely renewal compliance", rationale: "Completion of education alone does not resolve the separate need to handle renewal properly and on time.", isCorrect: true },
      { text: "Automatic conversion to appraiser status", rationale: "Licensing deadlines do not convert one profession into another.", isCorrect: false },
      { text: "Whether the property taxes were prorated", rationale: "Closing prorations are unrelated to license renewal filing.", isCorrect: false },
      { text: "Whether title passed by will", rationale: "Inheritance concepts do not control renewal timing.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 284,
    question: "A New York leasing office that keeps applicant screening documents should do so mainly because the records can help show:",
    answerOptions: [
      { text: "How decisions were handled and documented", rationale: "Organized leasing records help demonstrate what criteria and procedures were used in handling applications.", isCorrect: true },
      { text: "That all applicants were guaranteed approval", rationale: "Recordkeeping does not guarantee approval outcomes.", isCorrect: false },
      { text: "That no rent was ever due", rationale: "Screening records do not erase rent obligations.", isCorrect: false },
      { text: "That title was transferred at move-in", rationale: "Leasing does not transfer fee title.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 285,
    question: "A New York landlord evaluating reserve funds for an older building is focusing most directly on:",
    answerOptions: [
      { text: "Future repair and replacement needs", rationale: "Reserve planning is aimed at anticipated building costs for major repairs and replacements.", isCorrect: true },
      { text: "Changing the legal description", rationale: "Reserve funds are not used to alter legal descriptions.", isCorrect: false },
      { text: "Granting a use variance", rationale: "Variances are granted by local authorities, not through reserve budgeting.", isCorrect: false },
      { text: "Creating a deed covenant of seisin", rationale: "Title covenants are unrelated to reserve planning.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 286,
    question: "A New York tenant's right to occupy space under a lease is best described as:",
    answerOptions: [
      { text: "A leasehold interest", rationale: "A tenant typically holds a leasehold interest, not fee ownership, under a rental agreement.", isCorrect: true },
      { text: "Fee simple absolute", rationale: "That is ownership, not tenancy.", isCorrect: false },
      { text: "A remainder interest", rationale: "A remainder is a future estate concept, not a standard tenant right.", isCorrect: false },
      { text: "A blanket easement", rationale: "An easement is a nonpossessory use right, not the normal tenancy interest.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 287,
    question: "A New York mortgage broker advertising low rates that are not actually available would most likely raise a concern involving:",
    answerOptions: [
      { text: "Misleading financing representation", rationale: "Advertising or quoting loan terms inaccurately can create compliance and consumer-protection concerns.", isCorrect: true },
      { text: "Lawful variance procedure", rationale: "Variance procedure concerns zoning, not loan advertising.", isCorrect: false },
      { text: "A deed covenant", rationale: "Deed covenants are unrelated to mortgage advertising accuracy.", isCorrect: false },
      { text: "Escheat", rationale: "Escheat is a property-transfer-by-law concept, not a financing advertising issue.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 288,
    question: "A New York owner who spends $40,000 on a capital improvement may generally expect that expenditure to affect:",
    answerOptions: [
      { text: "Adjusted basis", rationale: "Capital improvements are commonly associated with adjustment to basis rather than routine personal or licensing matters.", isCorrect: true },
      { text: "Whether the broker needs a pocket card", rationale: "Licensing identification is unrelated to capital improvement tax treatment.", isCorrect: false },
      { text: "Whether a tenant holds a leasehold", rationale: "A leasehold is not determined by the owner's basis.", isCorrect: false },
      { text: "Whether the county records a deed", rationale: "Recording procedures are separate from basis calculation.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 289,
    question: "A New York broker setting a policy that agents should not meet unknown prospects alone in isolated locations is addressing:",
    answerOptions: [
      { text: "Licensee safety", rationale: "This is a straightforward personal-safety policy aimed at reducing risk during field activity.", isCorrect: true },
      { text: "Transfer tax allocation", rationale: "Closing tax allocation is unrelated to in-person showing safety.", isCorrect: false },
      { text: "Fee simple defeasance", rationale: "Estate concepts do not describe personal safety policy.", isCorrect: false },
      { text: "Title insurance underwriting", rationale: "Title underwriting is unrelated to agent meeting protocol.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 290,
    question: "A New York landlord comparing actual income to projected budgeted income is most directly performing:",
    answerOptions: [
      { text: "An operating performance review", rationale: "Comparing actual results against budget is a basic property-management performance control process.", isCorrect: true },
      { text: "A deed warranty analysis", rationale: "Title warranties are unrelated to property budget performance review.", isCorrect: false },
      { text: "A zoning variance hearing", rationale: "Budget comparison is not a zoning proceeding.", isCorrect: false },
      { text: "A mortgage satisfaction filing", rationale: "Lien release filing does not describe budget-versus-actual analysis.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 291,
    question: "A New York salesperson reviewing renewal status should understand that a lapse in required education can most directly affect:",
    answerOptions: [
      { text: "Eligibility to renew the license", rationale: "Continuing education is tied to renewal eligibility, so missing the requirement can interfere with renewal.", isCorrect: true },
      { text: "The county's tax levy", rationale: "License education does not affect local tax rates.", isCorrect: false },
      { text: "The title insurance premium", rationale: "Title premiums are unrelated to a salesperson's CE status.", isCorrect: false },
      { text: "The zoning district classification", rationale: "Land-use classifications do not depend on license renewal compliance.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 292,
    question: "A New York broker who trains agents to avoid statements they cannot verify is emphasizing:",
    answerOptions: [
      { text: "Professional competence and accuracy", rationale: "Professional-practice questions often center on accuracy, reasonable basis for statements, and avoiding misleading representations.", isCorrect: true },
      { text: "Automatic dual agency", rationale: "Dual agency requires consent and is unrelated to verifying property statements.", isCorrect: false },
      { text: "Fee simple defeasance", rationale: "Estate concepts are not the issue here.", isCorrect: false },
      { text: "Rectangular survey practice", rationale: "Survey description methods do not address truthful brokerage communication.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 293,
    question: "A New York agent asked to meet a new prospect at a vacant property should follow the safest practice by:",
    answerOptions: [
      { text: "Confirming the appointment with the office and identifying the prospect", rationale: "Basic field safety includes office awareness of the appointment and reasonable prospect identification.", isCorrect: true },
      { text: "Going alone without notifying anyone", rationale: "That increases risk rather than reducing it.", isCorrect: false },
      { text: "Leaving the property unlocked in advance", rationale: "That is unsafe and improper.", isCorrect: false },
      { text: "Sharing lockbox codes before meeting", rationale: "Providing access codes without control raises safety and security concerns.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 294,
    question: "A New York investor comparing properties by dividing NOI by price is most directly evaluating:",
    answerOptions: [
      { text: "Capitalization rate", rationale: "Cap rate is generally calculated by dividing NOI by value or price.", isCorrect: true },
      { text: "Loan-to-value ratio", rationale: "LTV compares loan amount to value, not NOI to price.", isCorrect: false },
      { text: "Gross income multiplier tax rate", rationale: "That is not the standard ratio described here.", isCorrect: false },
      { text: "Assessed value ratio", rationale: "Assessment ratios do not use NOI divided by price.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 295,
    question: "A special assessment on a New York property is most commonly tied to:",
    answerOptions: [
      { text: "A specific public improvement benefiting the property", rationale: "Special assessments are commonly charged to properties benefiting from specific public improvements.", isCorrect: true },
      { text: "A broker's renewal fee", rationale: "Licensing fees are unrelated to property assessments.", isCorrect: false },
      { text: "A title insurance claim", rationale: "Title claims are not the source of special assessments.", isCorrect: false },
      { text: "A tenant's security deposit", rationale: "Security deposits are lease-related, not public improvement charges.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 296,
    question: "A New York seller who actively hides a cracked foundation wall from buyers most directly creates a problem involving:",
    answerOptions: [
      { text: "Concealment of a material defect", rationale: "Actively hiding a serious physical problem creates a disclosure and concealment issue.", isCorrect: true },
      { text: "A legal description error only", rationale: "This is a physical-condition problem, not just a title-description issue.", isCorrect: false },
      { text: "A lawful tax exemption", rationale: "Concealed defects do not create tax benefits.", isCorrect: false },
      { text: "An automatic mortgage satisfaction", rationale: "Lien release has nothing to do with hidden structural damage.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 297,
    question: "At a New York closing, deed recording charges are most directly associated with:",
    answerOptions: [
      { text: "Transferring and documenting title", rationale: "Recording charges are part of the process of documenting the transfer of title in the public record.", isCorrect: true },
      { text: "Advertising a future open house", rationale: "Marketing has nothing to do with deed recording costs.", isCorrect: false },
      { text: "Calculating NOI", rationale: "Investment income analysis is unrelated to recording charges.", isCorrect: false },
      { text: "Creating a buyer's agency disclosure", rationale: "Agency disclosures are separate from title-recording expenses.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 298,
    question: "A New York borrower comparing a 95% LTV loan with an 80% LTV loan should expect the 95% LTV loan to involve:",
    answerOptions: [
      { text: "A smaller down payment", rationale: "A higher LTV means the loan covers more of the value or price, leaving less for the buyer to put down.", isCorrect: true },
      { text: "A larger down payment", rationale: "That is the opposite of what a higher LTV implies.", isCorrect: false },
      { text: "No connection to the purchase price", rationale: "LTV is directly related to value or price.", isCorrect: false },
      { text: "Automatic exemption from closing costs", rationale: "LTV does not erase closing costs.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 299,
    question: "A New York broker maintaining written office procedures for handling deposits, disclosures, and offers is focusing on:",
    answerOptions: [
      { text: "Compliance and supervision", rationale: "Written procedures are a core supervision tool for consistent and lawful brokerage operations.", isCorrect: true },
      { text: "Creating a tax lien", rationale: "Office procedure manuals do not create liens.", isCorrect: false },
      { text: "Changing local zoning", rationale: "Broker supervision is unrelated to legislative zoning changes.", isCorrect: false },
      { text: "Extending a mortgage commitment", rationale: "Loan commitment timing is separate from internal brokerage procedures.", isCorrect: false }
    ],
    hint: ""
  },
  {
    questionNumber: 300,
    question: "A New York office that keeps an itinerary of agent showings and check-ins is most directly addressing:",
    answerOptions: [
      { text: "Licensee safety procedures", rationale: "Tracking showings and expected check-ins is a basic safety-control practice.", isCorrect: true },
      { text: "Transfer tax proration", rationale: "Closing tax allocations are unrelated to showing itineraries.", isCorrect: false },
      { text: "Appraisal depreciation", rationale: "Appraisal concepts are not the point of office check-in policies.", isCorrect: false },
      { text: "A title abstract update", rationale: "Title history review is unrelated to field safety logistics.", isCorrect: false }
    ],
    hint: ""
  }
];
